import { useEffect, useState, useRef } from "react";
import api, { apiError, uploadFile, fileUrl } from "@/lib/api";
import BarcodeInput from "@/components/BarcodeInput";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { TRY, NUM, pname, fmtDate, fmtDateTime, unitLabel } from "@/lib/format";
import { StatCard, Empty, Cell } from "@/components/Shared";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Pencil, Trash2, Package, Wallet, AlertTriangle, Boxes, ClipboardEdit, Search,
  ImagePlus, Loader2, Tag, Barcode, ScanLine, X, Coins, FileText, ArrowRight, Clock, User, ClipboardList, PackagePlus, Building2,
  List, Filter, ChevronLeft, ChevronRight, LayoutGrid, Table2, Sparkles, FileSpreadsheet, Download, Camera, Eye, TrendingUp,
  History, RotateCcw, ShoppingCart, CheckCircle2, RefreshCw, Inbox, Cable, BadgePercent, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import ProductPicker from "@/components/ProductPicker";
import ProductImportDialog from "@/components/ProductImportDialog";
import StockApprovals from "@/components/stock/StockApprovals";
import StockValueReport from "@/components/stock/StockValueReport";
import QuickCountDialog from "@/components/stock/QuickCountDialog";
import StockReportDialog from "@/components/stock/StockReportDialog";
import StockPortal from "@/components/stock/portal/StockPortal";
import PurchaseHistoryTab from "@/components/stock/purchase/PurchaseHistoryTab";
import { PurchaseHistoryPanel } from "@/components/stock/purchase/PurchaseHistoryPanel";
import { StockSplit } from "@/components/StockSplit";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTrFont } from "@/lib/pdfFont";
import { RecordHistory } from "@/components/RecordHistory";
import { DatePicker } from "@/components/DatePicker";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { confirm } from "@/components/ConfirmDialog";
import { discountFor, discountKeys, productDiscountFields } from "@/lib/discountKey";
import { invalidateProductIndex } from "@/lib/productIndex";
import QuoteEditorDialog, { blankQuote } from "@/components/QuoteEditorDialog";
import { useNavigate, useLocation } from "react-router-dom";

const UNITS = ["ADET", "METRE", "KİLO", "PAKET", "KUTU", "TOP", "RULO", "METRETÜL", "TAKIM", "ÇİFT"];
const moveLabel = { giris: "Giriş", cikis: "Çıkış", sayim: "Sayım", transfer: "Aktarma", olusturma: "Yeni Ürün", silme: "Silindi", duzeltme: "Düzeltme", hizli_satis: "Hızlı Satış", hizli_satis_iptal: "Satış İptali" };
// Depo/Mağaza ayrıntısı (yalnız bölüm bilgisi kaydedilmiş hareketlerde)
const locInfo = (m) => {
  if (m.new_mag == null || m.old_mag == null) return "";
  if (m.type === "transfer") return `${m.location === "magaza" ? "Depo → Mağaza" : "Mağaza → Depo"} · Mağaza ${NUM(m.old_mag)} → ${NUM(m.new_mag)}`;
  if (m.location === "magaza") return `Mağaza ${NUM(m.old_mag)} → ${NUM(m.new_mag)}`;
  if (["giris", "cikis", "sayim"].includes(m.type)) return `Depo ${NUM((m.old_qty || 0) - m.old_mag)} → ${NUM((m.new_qty || 0) - m.new_mag)}`;
  return "";
};
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "-"; } };
const fmtD = (iso) => { try { const [y, m, d] = String(iso).split("-"); return `${d}.${m}.${y}`; } catch { return iso; } };
const RATE4 = (v) => new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(Number(v || 0));
// TCMB kurları her iş günü 15:30'da ilan eder → bugünkü bülten uygulandı mı? (İstanbul saatiyle)
const istNow = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", weekday: "short", hourCycle: "h23" }).formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { today: `${p.day}.${p.month}.${p.year}`, mins: Number(p.hour) * 60 + Number(p.minute), weekend: p.weekday === "Sat" || p.weekday === "Sun" };
};
const rateStatusOf = (r) => {
  const { today, mins, weekend } = istNow();
  if (r?.source === "tcmb_auto" && r?.tcmb_date === today) return { ok: true, label: "Güncel · bugünkü TCMB kuru" };
  if (!weekend && mins >= 15 * 60 + 30) return { ok: false, label: "Bugünkü TCMB kuru henüz uygulanmadı" };
  return { ok: true, label: r?.source === "tcmb_auto" ? "Güncel · son iş günü TCMB kuru" : "Elle girilen kur" };
};
const rateWhen = (r) => `${r?.applied_at ? fmtDateTime(r.applied_at) : "—"}${r?.source === "tcmb_auto" ? ` · TCMB ${r.tcmb_date} bülteni` : r?.applied_by ? ` · elle (${r.applied_by})` : ""}`;
const moveMeta = (t) => ({
  giris: { label: "Giriş", cls: "bg-[#16A34A] hover:bg-[#16A34A]", stripe: "border-l-emerald-500" },
  cikis: { label: "Çıkış", cls: "bg-destructive hover:bg-destructive", stripe: "border-l-red-500" },
  sayim: { label: "Sayım", cls: "bg-blue-600 hover:bg-blue-600", stripe: "border-l-blue-500" },
  transfer: { label: "Aktarma", cls: "bg-teal-600 hover:bg-teal-600 text-white", stripe: "border-l-teal-500" },
  olusturma: { label: "Yeni Ürün", cls: "bg-primary hover:bg-primary", stripe: "border-l-primary" },
  silme: { label: "Silindi", cls: "bg-zinc-500 hover:bg-zinc-500", stripe: "border-l-slate-400" },
  duzeltme: { label: "Düzeltme", cls: "bg-amber-500 hover:bg-amber-500 text-white", stripe: "border-l-amber-500" },
  hizli_satis: { label: "Hızlı Satış", cls: "bg-orange-600 hover:bg-orange-600 text-white", stripe: "border-l-orange-600" },
  hizli_satis_iptal: { label: "Satış İptali", cls: "bg-emerald-600 hover:bg-emerald-600 text-white", stripe: "border-l-emerald-600" },
}[t] || { label: t, cls: "bg-secondary", stripe: "border-l-secondary" });
const empty = { name: "", brand: "", series: "", code: "", barcode: "", barcodes: [], image_url: "", images: [], description: "", category: "", discount_group: "", unit: "ADET", quantity: 0, qty_depo: 0, qty_magaza: 0, critical_level: 0, list_price: 0, currency: "TL", price: 0, cost: 0, kdv_rate: 20, price_kdv_included: false, cost_manual: "", cost_source: "auto", price_general: "", price_elec_vat: "", price_elec_novat: "" };

// Kablo tespiti (makara/rulo takibi yalnız kablolarda) — backend CABLE_RE ile aynı desen
const CABLE_RE = /(NYAF|NYA|NYM|NYY|NAYY|NHXMH|N2XH|H07V|H05V|TTR|KABLO)/i;
const isCableProduct = (p) => !!p && CABLE_RE.test([p.discount_group, p.brand, p.name, p.category].filter(Boolean).join(" "));

export default function Stock() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [results, setResults] = useState(null);   // null=henüz arama yok
  const [mode, setMode] = useState("search");       // search | critical
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [brandSummary, setBrandSummary] = useState([]);
  const [rates, setRates] = useState({ USD: 0, EUR: 0, TL: 1 });
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [formSnapshot, setFormSnapshot] = useState("");
  const [uploading, setUploading] = useState(false);
  const [moveDlg, setMoveDlg] = useState(null);
  const [move, setMove] = useState({ type: "giris", quantity: 0, note: "" });
  const [scanOpen, setScanOpen] = useState(false);
  const [kurDlg, setKurDlg] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [kurForm, setKurForm] = useState({ USD: "", EUR: "" });
  const [tab, setTab] = useState("sorgu");   // sorgu | liste
  const [facets, setFacets] = useState({ brands: [], categories: [], series: [], series_by_brand: {}, categories_by_brand: {} });
  const [filters, setFilters] = useState({ q: "", brand: "", category: "", series: "" });
  const [histOpen, setHistOpen] = useState(false);
  const [browse, setBrowse] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [browseLoading, setBrowseLoading] = useState(false);
  const [sort, setSort] = useState("qty_desc");
  const [selected, setSelected] = useState([]);
  const [requestedIds, setRequestedIds] = useState([]);
  const [approvalCount, setApprovalCount] = useState(0);
  const loadApprovalCount = () => api.get("/stock/approvals/count").then((r) => setApprovalCount(r.data.count || 0)).catch(() => {});
  const [discMap, setDiscMap] = useState({});
  const [noPrice, setNoPrice] = useState({ items: [], loading: false });
  const [npQuery, setNpQuery] = useState("");
  const [npExpand, setNpExpand] = useState(null);
  const [npForm, setNpForm] = useState({});
  const [seriesDiscOpen, setSeriesDiscOpen] = useState(false);
  const [seriesDiscForm, setSeriesDiscForm] = useState({ cost_discount: "", sale_discount: "", electrician_discount: "", electrician_nobill_discount: "" });
  const [aiLoading, setAiLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [scanTarget, setScanTarget] = useState("search");
  const [quickOpen, setQuickOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [portalKey, setPortalKey] = useState(0);
  const [importing, setImporting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const suggestWrapRef = useRef(null);
  useEffect(() => {   // açılır liste: odak kaybında değil, DIŞINA dokunulunca kapanır (mobilde listeyi kaydırırken kapanmaz)
    if (!suggestOpen) return;
    const onDown = (e) => { if (suggestWrapRef.current && !suggestWrapRef.current.contains(e.target)) setSuggestOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [suggestOpen]);
  const [moves, setMoves] = useState([]);
  const [movesLoading, setMovesLoading] = useState(false);
  const [moveTypeFilter, setMoveTypeFilter] = useState("");
  const [moveStart, setMoveStart] = useState("");
  const [moveEnd, setMoveEnd] = useState("");
  const [quoteInit, setQuoteInit] = useState(null);
  const [qContacts, setQContacts] = useState([]);
  const navigate = useNavigate();
  const loadQContacts = () => api.get("/contacts").then((r) => setQContacts(r.data)).catch(() => {});
  const addToQuote = async (p) => {
    let disc = 0;
    try { const { data } = await api.get("/discounts/resolve?contact_title="); const d = discountFor(p, data.map); disc = d != null ? d : Number(data.default || 0); } catch { disc = 0; }
    const item = { name: p.name, description: "", code: p.code || "", quantity: 1, unit: unitLabel(p.unit, "ADET"), unit_price: Number(p.price) || 0, discount_rate: disc, cost: Number(p.cost) || 0, product_id: p.id, ...productDiscountFields(p) };
    setDetail(null);
    loadQContacts();
    setQuoteInit({ ...blankQuote(), items: [item] });
  };
  const onQuoteSaved = (qt) => {
    setQuoteInit(null);
    toast.success(`Teklif ${qt.quote_no} oluşturuldu`, { action: { label: "Teklifleri Aç", onClick: () => navigate("/teklifler") } });
  };

  const loadFacets = () => api.get("/products/facets").then((r) => setFacets(r.data)).catch(() => {});
  const loadBrowse = async (page = 1, silent = false) => {
    if (!silent) setBrowseLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.q.trim()) p.append("q", filters.q.trim());
      if (filters.brand) p.append("brand", filters.brand);
      if (filters.category) p.append("category", filters.category);
      if (filters.series) p.append("series", filters.series);
      p.append("sort", sort);
      p.append("page", page); p.append("page_size", 50);
      const { data } = await api.get(`/products/browse?${p.toString()}`);
      setBrowse(data); setSelected([]);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBrowseLoading(false); }
  };
  useEffect(() => {
    if (tab === "liste") { loadFacets(); loadBrowse(1); }
    // eslint-disable-next-line
  }, [tab, filters.brand, filters.category, filters.series, sort]);
  useAutoRefresh(() => {
    if (tab === "liste") { loadBrowse((browse && browse.page) || 1, true); loadFacets(); }
    if (tab === "hareketler") loadMoves(true);
    loadApprovalCount();
    if (isAdmin) api.get("/orders/active-product-ids").then((r) => setRequestedIds(r.data.product_ids || [])).catch(() => {});
  }, { interval: 8000 });
  useEffect(() => { if (tab === "hareketler" && isAdmin) loadMoves(); /* eslint-disable-next-line */ }, [tab, moveTypeFilter, moveStart, moveEnd]);
  useEffect(() => { if (tab === "fiyatsiz" && isAdmin) loadNoPrice(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { if (!dlg && tab === "fiyatsiz" && isAdmin) loadNoPrice(); /* eslint-disable-next-line */ }, [dlg]);

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === browse.items.length ? [] : browse.items.map((p) => p.id));
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!await confirm(`${selected.length} ürünü kalıcı olarak silmek istediğinize emin misiniz?`)) return;
    try {
      const { data } = await api.post("/products/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} ürün silindi`);
      setSelected([]); loadBrowse(browse.page); loadStats();
    } catch (e) { toast.error(apiError(e)); }
  };

  const loadStats = () => { if (isAdmin) api.get("/products/stats").then((r) => setStats(r.data)).catch(() => {}); };
  const loadBrandSummary = () => { if (isAdmin) api.get("/products/brand-summary").then((r) => setBrandSummary(r.data.brands || [])).catch(() => {}); };
  const loadRates = () => api.get("/settings/rates").then((r) => setRates(r.data)).catch(() => {});
  const loadNoPrice = async () => {
    setNoPrice((s) => ({ ...s, loading: true }));
    try { const { data } = await api.get("/products/no-price"); setNoPrice({ items: data, loading: false }); setNpExpand(null); }
    catch (e) { toast.error(apiError(e)); setNoPrice((s) => ({ ...s, loading: false })); }
  };
  const openNpRow = (p) => {
    if (npExpand === p.id) { setNpExpand(null); return; }
    setNpExpand(p.id);
    setNpForm({ price_general: p.price_general ?? "", price_elec_vat: p.price_elec_vat ?? "", price_elec_novat: p.price_elec_novat ?? "", cost_manual: p.cost_manual ?? "" });
  };
  const saveNoPrice = async (p) => {
    const body = {};
    ["price_general", "price_elec_vat", "price_elec_novat", "cost_manual"].forEach((k) => { body[k] = npForm[k] === "" || npForm[k] == null ? null : Number(npForm[k]); });
    const anySale = ["price_general", "price_elec_vat", "price_elec_novat"].some((k) => body[k] != null && body[k] > 0);
    if (!anySale && body.cost_manual == null) return toast.error("En az bir satış fiyatı girin");
    try {
      await api.put(`/products/${p.id}/manual-price`, body);
      toast.success(`Fiyat kaydedildi: ${p.name}`);
      invalidateProductIndex(); setNpExpand(null); loadStats();
      if (anySale) setNoPrice((s) => ({ ...s, items: s.items.filter((x) => x.id !== p.id) }));
      else loadNoPrice();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveSeriesDisc = async () => {
    const g = (form.series || "").trim();
    if (!g) return toast.error("Önce seri adı girin");
    const body = { group: g, cost_discount: Number(seriesDiscForm.cost_discount) || 0, sale_discount: Number(seriesDiscForm.sale_discount) || 0, electrician_discount: Number(seriesDiscForm.electrician_discount) || 0, electrician_nobill_discount: Number(seriesDiscForm.electrician_nobill_discount) || 0 };
    try {
      const { data } = await api.put("/discounts/global", body);
      toast.success(`"${g}" serisi iskontosu kaydedildi · ${data.updated} ürün güncellendi`);
      setDiscMap((m) => ({ ...m, [g]: { cost: body.cost_discount, sale: body.sale_discount } }));
      setSeriesDiscOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadStats(); loadBrandSummary(); loadRates(); loadFacets(); loadApprovalCount(); if (isAdmin) { api.get("/orders/active-product-ids").then((r) => setRequestedIds(r.data.product_ids || [])).catch(() => {}); api.get("/discounts").then((r) => { const mp = {}; (r.data.items || []).forEach((x) => { mp[x.group] = { cost: x.cost_discount || 0, sale: x.sale_discount || 0 }; }); setDiscMap(mp); }).catch(() => {}); } /* eslint-disable-next-line */ }, [isAdmin]);

  // Ara/Tarat sekmesinde yazarken canlı sonuç (kısa debounce + bayat yanıt koruması)
  const reqSeq = useRef(0);
  useEffect(() => {
    if (tab !== "sorgu") return;
    const t = q.trim();
    if (!t) { if (mode === "search") setResults(null); return; }
    const id = setTimeout(() => { search(t); }, 120);
    return () => clearTimeout(id);
    // eslint-disable-next-line
  }, [q, tab]);

  const search = async (term) => {
    const t = (term ?? q).trim();
    if (!t) { setResults(null); return; }
    const seq = ++reqSeq.current;
    setLoading(true); setMode("search");
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(t)}`);
      if (seq !== reqSeq.current) return;   // daha yeni bir arama başladı → bu yanıtı yoksay
      setResults(data);
    }
    catch (e) { if (seq === reqSeq.current) toast.error(apiError(e)); }
    finally { if (seq === reqSeq.current) setLoading(false); }
  };
  const showCritical = async () => {
    setLoading(true); setMode("critical"); setQ("");
    try { const { data } = await api.get("/products/critical"); setResults(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  const refresh = () => { if (tab === "portal") setPortalKey((k) => k + 1); else if (tab === "liste") loadBrowse(browse.page); else if (tab === "hareketler") loadMoves(); else if (mode === "critical") showCritical(); else if (q.trim()) search(); loadStats(); };
  const moveParams = (limit) => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (moveTypeFilter) p.set("type", moveTypeFilter);
    if (moveStart) p.set("start", moveStart);
    if (moveEnd) p.set("end", moveEnd);
    return p.toString();
  };
  const loadMoves = async (silent = false) => {
    if (!silent) setMovesLoading(true);
    try { const { data } = await api.get(`/stock/movements?${moveParams(200)}`); setMoves(data); }
    catch (e) { if (!silent) toast.error(apiError(e)); }
    finally { if (!silent) setMovesLoading(false); }
  };
  const undoMove = async (m) => {
    if (!await confirm("Bu stok hareketini geri almak istediğinize emin misiniz? Ürün miktarı düzeltilecek.")) return;
    try { await api.post(`/stock/movements/${m.id}/undo`); toast.success("Hareket geri alındı, stok düzeltildi"); loadMoves(); loadStats(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportMovesExcel = async () => {
    try {
      const { data: rows } = await api.get(`/stock/movements?${moveParams(5000)}`);
      if (!rows.length) return toast.error("Dışa aktarılacak hareket yok");
      const data = rows.map((m) => ({
        "Tarih": fmtDT(m.created_at), "Tür": moveLabel[m.type] || m.type, "Ürün": m.product_name,
        "Eski": m.old_qty, "Yeni": m.new_qty, "Değişim": (m.new_qty ?? 0) - (m.old_qty ?? 0),
        "Kullanıcı": m.user_name || "", "Not": m.note || "",
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [{ wch: 20 }, { wch: 12 }, { wch: 42 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 18 }, { wch: 44 }];
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Stok Hareketleri");
      XLSX.writeFile(wb, "stok-hareketleri.xlsx");
      toast.success(`${data.length} hareket Excel'e aktarıldı`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportMovesPdf = async () => {
    try {
      const { data: rows } = await api.get(`/stock/movements?${moveParams(5000)}`);
      if (!rows.length) return toast.error("Dışa aktarılacak hareket yok");
      const pdf = new jsPDF("p", "mm", "a4");
      const FONT = await registerTrFont(pdf);
      const pageW = pdf.internal.pageSize.getWidth();
      const M = 12; const contentW = pageW - M * 2;
      const NAVY = [11, 15, 25]; const ORANGE = [234, 88, 32];
      const period = (moveStart || moveEnd) ? `${moveStart ? fmtD(moveStart) : "…"} - ${moveEnd ? fmtD(moveEnd) : "…"}` : "Tümü";
      const drawHeader = () => {
        pdf.setFillColor(...ORANGE); pdf.roundedRect(M, 12, 3.5, 13, 1, 1, "F");
        pdf.setFont(FONT, "bold"); pdf.setFontSize(17); pdf.setTextColor(20, 20, 20);
        pdf.text("GÜMÜŞALAN ELEKTRİK", M + 7, 19);
        pdf.setFont(FONT, "normal"); pdf.setFontSize(8.5); pdf.setTextColor(150, 150, 150);
        pdf.text("Stok Hareket Dökümü", M + 7, 24);
        pdf.setFont(FONT, "bold"); pdf.setFontSize(12); pdf.setTextColor(...ORANGE);
        pdf.text("STOK HAREKETLERİ", pageW - M, 18, { align: "right" });
        pdf.setFont(FONT, "normal"); pdf.setFontSize(8.5); pdf.setTextColor(150, 150, 150);
        pdf.text(`Dönem: ${period}`, pageW - M, 23.5, { align: "right" });
        pdf.setDrawColor(235, 235, 235); pdf.setLineWidth(0.4); pdf.line(M, 31, pageW - M, 31);
      };
      const body = rows.map((m) => [fmtDT(m.created_at), moveLabel[m.type] || m.type, m.product_name, `${NUM(m.old_qty)} → ${NUM(m.new_qty)}`, m.user_name || "—", m.note || ""]);
      autoTable(pdf, {
        head: [["Tarih", "Tür", "Ürün", "Miktar", "Kullanıcı", "Not"]],
        body, startY: 35, margin: { left: M, right: M, top: 35 },
        styles: { font: FONT, fontSize: 7.5, cellPadding: 1.6, textColor: [45, 45, 45], lineColor: [235, 235, 235], lineWidth: 0.1, overflow: "linebreak", valign: "middle" },
        headStyles: { font: FONT, fontStyle: "bold", fillColor: NAVY, textColor: [255, 255, 255], fontSize: 7.5 },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 18 }, 2: { cellWidth: contentW - 30 - 18 - 26 - 24 - 30 }, 3: { cellWidth: 26 }, 4: { cellWidth: 24 }, 5: { cellWidth: 30 } },
        didDrawPage: () => drawHeader(),
      });
      pdf.setFont(FONT, "normal"); pdf.setFontSize(7.5); pdf.setTextColor(180, 180, 180);
      pdf.text(`Toplam ${rows.length} hareket`, pageW / 2, pdf.internal.pageSize.getHeight() - 8, { align: "center" });
      pdf.save("stok-hareketleri.pdf");
      toast.success(`${rows.length} hareket PDF'e aktarıldı`);
    } catch (e) { console.error(e); toast.error("PDF oluşturulamadı"); }
  };

  const genAiImage = async () => {
    if (!editId) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/products/${editId}/ai-image`);
      const images = (data.images && data.images.length) ? data.images : (data.image_url ? [data.image_url] : []);
      setForm((f) => ({ ...f, images }));
      toast.success("AI görsel üretildi");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAiLoading(false); }
  };

  const openNew = (barcode = "") => {
    const f = { ...empty, images: [], barcodes: [], barcode };
    setForm(f); setFormSnapshot(JSON.stringify(f)); setEditId(null); setDlg(true);
  };
  const openEdit = (p) => {
    const images = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
    const nn = (v) => (v == null ? "" : v);
    const mag = Math.round((Number(p.qty_magaza) || 0) * 100) / 100;
    const f = { ...empty, ...p, images, barcodes: p.barcodes || [],
      qty_magaza: mag, qty_depo: Math.round(((Number(p.quantity) || 0) - mag) * 100) / 100,
      orig_quantity: Number(p.quantity) || 0, orig_qty_magaza: mag,
      kdv_rate: p.kdv_rate != null ? p.kdv_rate : 20,
      price_kdv_included: p.price_kdv_included != null ? p.price_kdv_included : false,
      cost_manual: nn(p.cost_manual), price_general: nn(p.price_general), price_elec_vat: nn(p.price_elec_vat), price_elec_novat: nn(p.price_elec_novat) };
    setForm(f); setFormSnapshot(JSON.stringify(f)); setEditId(p.id); setDlg(true);
  };
  const openProductByCode = async (code) => {
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}`);
      const exact = data.find((r) => [r.product.code, r.product.barcode, ...(r.product.barcodes || [])].some((b) => String(b || "").toLowerCase() === String(code).toLowerCase())) || data[0];
      if (exact) { openEdit(exact.product); toast.success("Ürün açıldı: " + exact.product.name); return true; }
      toast.error("Ürün bulunamadı: " + code); return false;
    } catch { toast.error("Arama hatası"); return false; }
  };
  // Fiziksel okuyucudan (?scan=) veya kamera taramasından gelen barkodla ürünü aç
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const code = sp.get("scan");
    if (!code) return;
    openProductByCode(code);
    sp.delete("scan");
    navigate({ pathname: "/stok", search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
    // eslint-disable-next-line
  }, [location.search]);
  const tryCloseDlg = async (open) => {
    if (open) { setDlg(true); return; }
    if (JSON.stringify(form) !== formSnapshot) {
      if (!await confirm("Kaydedilmemiş değişiklikler var. Bu pencereyi kapatırsanız girdiğiniz bilgiler kaybolacaktır. Çıkmak istediğinize emin misiniz?")) return;
    }
    setDlg(false);
  };
  const addBarcode = () => setForm((f) => ({ ...f, barcodes: [...(f.barcodes || []), ""] }));
  const setBarcodeAt = (i, v) => setForm((f) => ({ ...f, barcodes: (f.barcodes || []).map((b, idx) => (idx === i ? v : b)) }));
  const rmBarcode = (i) => setForm((f) => ({ ...f, barcodes: (f.barcodes || []).filter((_, idx) => idx !== i) }));

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) { const { url } = await uploadFile(f); urls.push(url); }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...urls] }));
      toast.success(`${urls.length} fotoğraf yüklendi`);
    } catch (err) { toast.error("Yükleme başarısız: " + apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const handleScan = async (code) => {
    setScanOpen(false);
    if (scanTarget === "edit") { setScanTarget("search"); openProductByCode(code); return; }
    if (scanTarget === "barcode") { setForm((f) => ({ ...f, barcode: code })); setScanTarget("search"); toast.success("Barkod okundu: " + code); return; }
    if (scanTarget === "extra-barcode") { setForm((f) => ({ ...f, barcodes: [...(f.barcodes || []), code] })); setScanTarget("search"); toast.success("Ek barkod eklendi: " + code); return; }
    setQ(code);
    setLoading(true); setMode("search");
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}`);
      setResults(data);
      const exact = data.find((r) => (r.product.barcode || "").trim() === code || (r.product.code || "").trim() === code);
      if (exact) {
        toast.success(`Ürün bulundu: ${exact.product.name} · ${NUM(exact.product.quantity)} ${exact.product.unit}`);
        setMove({ type: "giris", quantity: 0, note: `Barkod: ${code}` });
        setMoveDlg(exact.product);
      } else if (!data.length) {
        toast.error(`"${code}" bulunamadı — yeni ürün ekleyin`);
        openNew(code);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const computedPrice = () => {
    const rate = rates[form.currency] || 1;
    let lp = Number(form.list_price) || 0;
    if (form.price_kdv_included) {
      const k = Number(form.kdv_rate) || 0;
      lp = lp / (1 + k / 100);
    }
    return Math.round(lp * rate * 100) / 100;
  };
  const pricePreview = () => {
    const price = computedPrice();
    const keys = discountKeys(form);   // Seri ("MARKA › SERİ") > çıplak seri > çeşit > marka
    const g = keys.find((k) => discMap[k]) || (form.discount_group || form.brand || "").trim();
    const d = discMap[g] || { cost: 0, sale: 0 };
    const cost = Math.round(price * (1 - d.cost / 100) * 100) / 100;
    const sale = Math.round(price * (1 - d.sale / 100) * 100) / 100;
    const profit = Math.round((sale - cost) * 100) / 100;
    const margin = sale > 0 ? Math.round((profit / sale) * 100) : 0;
    return { price, cost, sale, profit, margin, d, g, hasDisc: !!discMap[g] };
  };
  const orderFromMove = async (m) => {
    const qty = Math.max(1, Math.round(((m.critical_level || 0) - (m.new_qty || 0)) * 100) / 100);
    try {
      await api.post("/orders", { product_id: m.product_id, product_name: m.product_name, requested_qty: qty, note: `Kritik stok — '${m.product_name}' kritik seviyenin (${NUM(m.critical_level)}) altına düştü` });
      toast.success("Sipariş talebi oluşturuldu");
      api.get("/orders/active-product-ids").then((r) => setRequestedIds(r.data.product_ids || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };
  const openQuick = () => setQuickOpen(true);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ürün adı gerekli");
    const price = computedPrice();
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      ...form,
      barcodes: (form.barcodes || []).map((b) => (b || "").trim()).filter(Boolean),
      unit: (form.unit || "ADET").toUpperCase(),
      quantity: Math.round(((Number(form.qty_depo) || 0) + (Number(form.qty_magaza) || 0)) * 100) / 100,
      qty_magaza: Number(form.qty_magaza) || 0, critical_level: Number(form.critical_level),
      list_price: Number(form.list_price) || 0, currency: form.currency,
      kdv_rate: Number(form.kdv_rate) || 0, price_kdv_included: !!form.price_kdv_included,
      price, cost: price,
      cost_manual: numOrNull(form.cost_manual),
      price_general: numOrNull(form.price_general),
      price_elec_vat: numOrNull(form.price_elec_vat),
      price_elec_novat: numOrNull(form.price_elec_novat),
    };
    delete payload.drums;   // makara/rulo yalnız /drums uçlarından yönetilir
    delete payload.qty_depo;
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Ürün güncellendi" : "Ürün eklendi");
      invalidateProductIndex();
      setDlg(false); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!await confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    await api.delete(`/products/${id}`);
    invalidateProductIndex();
    toast.success("Ürün silindi"); refresh();
  };

  const openDetail = async (p) => {
    setDetailLoading(true);
    setDetail({ product: p, recent_movements: [], movement_count: 0, orders: [], stock_value: (p.cost || 0) * Math.max(0, p.quantity || 0) });
    try {
      const { data } = await api.get(`/products/lookup?id=${encodeURIComponent(p.id)}`);
      const found = data.find((r) => r.product.id === p.id);   // yalnızca tıklanan ürün; metin eşleşmesiyle başka ürün AÇILMAZ
      if (found) setDetail(found);
    } catch { /* temel bilgiyle göster */ }
    finally { setDetailLoading(false); }
  };

  const downloadTemplate = () => {
    const H = ["MARKA", "Seri Adı", "Ürün Grubu", "Alt Ürün Grubu", "Ürün Kodu", "Ürün Adı", "BARKOD 1", "BARKOD 2", "BARKOD 3", "LİSTE FİYATI", "Para Birimi", "KOLİ ADEDİ", "BİRİM", "Ürün Resmi"];
    const rows = [
      ["VİKO", "Karre", "Anahtar & Priz", "Priz", "90967019", "Karre Topraklı Priz Beyaz", "8690000000001", "", "", 250, "TL", 50, "ADET", "https://ornek.com/resim.jpg (veya hücreye resim gömün)"],
      ["PANASONIC", "Thea", "Anahtar & Priz", "Anahtar", "WMTC0001-2WH", "Thea Blu Anahtar Beyaz", "8690000000002", "", "", 4.5, "USD", 100, "ADET", ""],
    ];
    const ws = XLSX.utils.aoa_to_sheet([H, ...rows]);
    ws["!cols"] = H.map((h) => ({ wch: Math.max(14, h.length + 2) }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Sayfa1");
    XLSX.writeFile(wb, "toplu-urun-ekleme-sablonu.xlsx");
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/products/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!data.rows?.length) { toast.error("Dosyada ürün satırı bulunamadı"); return; }
      setImportPreview(data);
      toast.success(`${data.total} satır okundu · ${data.existing} mevcut ürünle eşleşti${data.images ? ` · ${data.images} resim` : ""}`);
    } catch (err) { toast.error("İçe aktarma hatası: " + apiError(err)); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const fetchTcmbKur = async () => {
    try { const { data } = await api.get("/settings/rates/tcmb"); setKurForm({ USD: data.USD, EUR: data.EUR }); toast.success(`TCMB ${data.date} · USD ${data.USD} · EUR ${data.EUR}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveMove = async () => {
    try {
      await api.post("/stock/movements", { product_id: moveDlg.id, type: move.type, quantity: Number(move.quantity), note: move.note, location: move.location || "depo" });
      toast.success("Stok hareketi kaydedildi");
      setMoveDlg(null); setMove({ type: "giris", quantity: 0, note: "" }); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openKur = () => { setKurForm({ USD: rates.USD, EUR: rates.EUR }); setKurDlg(true); };
  const [kurBusy, setKurBusy] = useState(false);
  const applyTcmbNow = async () => {
    setKurBusy(true);
    try {
      const { data } = await api.post("/settings/rates/tcmb-apply");
      toast.success(`TCMB ${data.date} · USD ${data.USD} · EUR ${data.EUR} · ${data.updated} ürünün TL fiyatı yenilendi`);
      setKurDlg(false); loadRates(); refresh();
    } catch (e) { toast.error(apiError(e)); }
    finally { setKurBusy(false); }
  };
  const saveKur = async () => {
    try {
      const { data } = await api.put("/settings/rates", { USD: Number(kurForm.USD), EUR: Number(kurForm.EUR) });
      toast.success(`Kur güncellendi · ${data.updated} döviz ürünün TL fiyatı yenilendi`);
      setKurDlg(false); loadRates(); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 stagger-in" data-testid="stock-page">
      <div className="relative overflow-hidden rounded-3xl bg-[#0B192C] text-white p-6 md:p-7 shadow-xl" data-testid="stock-header">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#EA5820]/20 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EA5820]/40 bg-[#EA5820]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF8A4C]">Canlı Envanter</span>
            <h1 className="mt-2 font-head text-2xl md:text-4xl font-black tracking-tight flex items-center gap-2.5"><span className="grid h-10 w-10 md:h-11 md:w-11 place-items-center rounded-2xl bg-[#EA5820] shadow-lg shadow-[#EA5820]/40"><Boxes className="h-5 w-5 md:h-6 md:w-6" /></span>Stok Yönetimi &amp; Kataloğu</h1>
            <p className="text-sm text-white/60 mt-1.5 max-w-xl">Ürün kataloğu, ambar hareketleri ve kritik stok takibi · ara, tarat, gir, say.</p>
            {isAdmin && rates.source !== undefined && (() => { const st = rateStatusOf(rates); return (
              <button type="button" onClick={openKur} title="Döviz kuru ayrıntıları" className={cn("mt-3 inline-flex flex-wrap items-center gap-x-2.5 gap-y-0.5 rounded-xl border px-3 py-1.5 text-left text-xs transition-colors", st.ok ? "border-emerald-400/40 bg-emerald-400/10 hover:bg-emerald-400/15" : "border-amber-400/50 bg-amber-400/10 hover:bg-amber-400/15")} data-testid="rate-status">
                <span className={cn("h-2 w-2 rounded-full shrink-0", st.ok ? "bg-emerald-400" : "bg-amber-400 animate-pulse")} />
                <b className="text-white tabular-nums" data-testid="rate-status-values">USD {RATE4(rates.USD)} · EUR {RATE4(rates.EUR)}</b>
                <span className={st.ok ? "text-emerald-200" : "text-amber-200"} data-testid="rate-status-label">{st.label}</span>
                <span className="text-white/60" data-testid="rate-status-time">Son güncelleme: {rateWhen(rates)}</span>
              </button>
            ); })()}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {isAdmin && <Button variant="outline" onClick={openKur} data-testid="kur-btn" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"><Coins className="h-4 w-4 mr-2" />Döviz Kuru</Button>}
            <Button onClick={() => { setScanTarget("search"); setScanOpen(true); }} data-testid="scan-btn" className="bg-white/10 hover:bg-white/20 text-white border border-white/20"><ScanLine className="h-4 w-4 mr-2" />Barkod Tara</Button>
            {isAdmin && <Button variant="outline" onClick={downloadTemplate} data-testid="product-template-btn" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"><Download className="h-4 w-4 mr-2" />Şablon</Button>}
            {isAdmin && <label className="cursor-pointer inline-flex items-center justify-center h-10 px-4 rounded-md border border-white/20 bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors text-white" data-testid="product-import-label">{importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-400" />}Excel İçe Aktar<input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="product-import" /></label>}
            <Button onClick={() => openNew()} data-testid="add-product-btn" className="bg-[#EA5820] hover:bg-[#D84A15] text-white"><Plus className="h-4 w-4 mr-2" />Yeni Ürün</Button>
          </div>
        </div>
      </div>

      {isAdmin && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard label="Toplam Çeşit" value={NUM(stats.total_products)} icon={Boxes} sub="katalogdaki ürün" testid="stock-kpi-total-products" />
          <StatCard label="Toplam Adet" value={NUM(stats.total_units)} icon={Package} tone="blue" sub="tüm birimler" testid="stock-kpi-total-units" />
          <StatCard label="Stok Değeri (Maliyet)" value={TRY(stats.total_stock_value)} icon={Wallet} tone="primary" sub={stats.negative_count ? `${NUM(stats.negative_count)} eksi stok sıfır sayılır · en değerliyi gör` : `En değerliyi gör · USD ${rates.USD}`} testid="stock-value-card" onClick={() => { setSort("value_desc"); setTab("liste"); }} />
          <StatCard label="Tahmini Stok Kârı" value={TRY(stats.total_stock_profit || 0)} icon={TrendingUp} tone="success" sub="satış − maliyet" testid="stock-kpi-estimated-profit" />
          <StatCard label="Kritik Stok Uyarı" value={NUM(stats.critical_count)} icon={AlertTriangle} tone={stats.critical_count ? "danger" : "default"} sub="görüntülemek için tıkla" testid="critical-card" onClick={showCritical} active={mode === "critical" && tab === "sorgu"} />
        </div>
      )}

      {isAdmin && brandSummary.length > 0 && (
        <StockValueReport brands={brandSummary} stats={stats} onViewBrand={(brand) => { setFilters({ q: "", brand, category: "", series: "" }); setTab("liste"); setTimeout(() => document.querySelector('[data-testid="stock-tabs"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }} />
      )}

      {/* Görünüm sekmeleri */}
      <div className="inline-flex gap-1 bg-muted/60 rounded-xl p-1 flex-wrap" data-testid="stock-tabs">
        {[["sorgu", "Ara / Barkod Tarat", Search], ...(isAdmin ? [["portal", "Stok Portalı", Table2], ["alis", "Alış Geçmişi", ShoppingBag]] : []), ["liste", "Tüm Ürün Kataloğu", LayoutGrid], ...(isAdmin ? [["fiyatsiz", "Fiyatsız Ürünler", Coins], ["hareketler", "Canlı Stok Hareketleri", History]] : []), ["onay", "Onay Bekleyenler", Inbox]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={cn("px-4 py-2 rounded-lg text-sm font-semibold transition-colors inline-flex items-center gap-2 active:scale-[0.98]", tab === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} data-testid={`tab-${k}`}>
            <Icon className={cn("h-4 w-4", tab === k && "text-primary")} />{l}
            {k === "onay" && approvalCount > 0 && <span className="ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-black tabular-nums" data-testid="tab-onay-count">{approvalCount}</span>}
          </button>
        ))}
      </div>

      {tab === "onay" && <StockApprovals isAdmin={isAdmin} onChanged={() => { loadStats(); loadApprovalCount(); if (tab === "hareketler") loadMoves(true); }} />}

      {tab === "portal" && isAdmin && <StockPortal onOpenEdit={openEdit} reloadKey={portalKey} />}
      {tab === "alis" && isAdmin && <PurchaseHistoryTab />}

      {tab === "sorgu" && (
      <div className="space-y-6">
      <div className="bg-card border rounded-xl p-3 sm:p-4 shadow-sm flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1" ref={suggestWrapRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Ürün adı, stok kodu, OEM veya barkod ara..." value={q}
            onChange={(e) => { setQ(e.target.value); setSuggestOpen(true); }}
            onFocus={() => setSuggestOpen(true)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSuggestOpen(false); search(); } if (e.key === "Escape") setSuggestOpen(false); }}
            className="pl-9 h-11" data-testid="stock-search" />
          {suggestOpen && q.trim() && mode === "search" && Array.isArray(results) && results.length > 0 && (
            <div className="absolute z-30 mt-1 w-full rounded-lg border bg-popover shadow-lg overflow-hidden" data-testid="stock-search-dropdown">
              <div className="max-h-80 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
                {results.slice(0, 8).map((r) => {
                  const p = r.product;
                  const crit = p.critical_level > 0 && p.quantity <= p.critical_level;
                  const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
                  return (
                    <button type="button" key={p.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setSuggestOpen(false); openDetail(p); }}
                      className="w-full text-left px-3 py-2.5 hover:bg-muted/60 border-b last:border-0 transition-colors flex items-center gap-3" data-testid={`stock-suggest-${p.id}`}>
                      {imgs[0] ? <img src={fileUrl(imgs[0])} alt="" loading="lazy" decoding="async" className="h-10 w-10 rounded object-contain bg-white border shrink-0" />
                        : <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0"><Package className="h-4 w-4 text-muted-foreground/40" /></div>}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <span className="font-semibold text-sm leading-snug whitespace-normal break-words" title={pname(p)}>{pname(p)}</span>
                          {isAdmin && <span className="text-sm font-bold text-primary tabular-nums shrink-0">{TRY(r.sale_price != null ? r.sale_price : p.price)}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                          {p.brand && <span className="bg-secondary/10 text-secondary px-1.5 rounded">{p.brand}</span>}
                          {p.code && <span className="font-mono">{p.code}</span>}
                          <StockSplit p={p} total className={cn("ml-auto", crit && "ring-1 ring-destructive/50")} testid={`res-stock-${p.id}`} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {results.length > 8 && <div className="px-3 py-1.5 text-[11px] text-muted-foreground bg-muted/40">+{results.length - 8} sonuç daha · aşağıdaki listede görünür</div>}
            </div>
          )}
        </div>
        <Button className="h-11" onClick={() => search()} disabled={loading} data-testid="stock-search-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Sorgula
        </Button>
        <Button className="h-11" variant="outline" onClick={showCritical} data-testid="critical-btn"><AlertTriangle className="h-4 w-4 mr-2 text-destructive" />Kritik Stoklar</Button>
        <Button className="h-11 bg-secondary hover:bg-secondary/90 text-white" onClick={openQuick} data-testid="quick-count-btn"><ClipboardEdit className="h-4 w-4 mr-2" />Hızlı Sayım / Giriş</Button>
        {isAdmin && <Button className="h-11" variant="outline" onClick={() => setReportOpen(true)} data-testid="stock-report-btn"><FileText className="h-4 w-4 mr-2 text-primary" />Stok Raporu (PDF)</Button>}
      </div>

      {results === null ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed rounded-xl bg-card/50" data-testid="stock-empty-hint">
          <div className="mx-auto mb-3 h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><ScanLine className="h-7 w-7" /></div>
          <p className="font-head font-bold text-foreground/80">Ürün arayın veya barkod tarayın</p>
          <p className="text-sm mt-1">Kodu/barkodu girin; stokta olup olmadığını, fiyatını ve hareketlerini görün.</p>
        </div>
      ) : results.length === 0 ? (
        <Empty title={mode === "critical" ? "Kritik seviyede ürün yok" : "Aramanıza Uygun Ürün Bulunamadı"} desc={mode === "critical" ? "Tüm ürünler kritik seviyenin üzerinde." : `"${q}" için ürün bulunamadı. Filtreleri sıfırlayarak veya yeni ürün ekleyerek devam edebilirsiniz.`} action={mode !== "critical" && <Button size="sm" variant="outline" onClick={() => { setQ(""); setResults(null); }} data-testid="stock-empty-reset-filter-btn"><X className="h-4 w-4 mr-1.5" />Aramayı Sıfırla</Button>} />
      ) : (
        <>
          {mode === "critical" && <div className="inline-flex items-center gap-2 rounded-full bg-red-50 text-red-700 border border-red-200 px-3 py-1 text-sm font-semibold" data-testid="stock-critical-banner"><AlertTriangle className="h-4 w-4" />Kritik seviyedeki {results.length} ürün</div>}
          <div className="space-y-4">
            {results.map((r) => (
              <ProductCard key={r.product.id} data={r} isAdmin={isAdmin}
                onMove={(p) => { setMove({ type: "giris", quantity: 0, note: "" }); setMoveDlg(p); }}
                onEdit={openEdit} onDelete={del} onQuote={addToQuote} />
            ))}
          </div>
        </>
      )}
      </div>
      )}

      {tab === "liste" && (
        <div className="space-y-3" data-testid="stock-list-view">
          {/* Filtreler */}
          <div className="bg-card border rounded-xl p-3 shadow-sm flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Ürün adı / kod / barkod..." value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && loadBrowse(1)} className="pl-9 h-10" data-testid="list-search" />
            </div>
            <Select value={filters.brand || "__all"} onValueChange={(v) => setFilters({ ...filters, brand: v === "__all" ? "" : v, series: "", category: "" })}>
              <SelectTrigger className="sm:w-48 h-10" data-testid="list-brand"><SelectValue placeholder="Marka" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tüm Markalar</SelectItem>
                {facets.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.series || "__all"} onValueChange={(v) => setFilters({ ...filters, series: v === "__all" ? "" : v })}>
              <SelectTrigger className="sm:w-48 h-10" data-testid="list-series"><SelectValue placeholder="Seri" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tüm Seriler</SelectItem>
                {(filters.brand ? (facets.series_by_brand?.[filters.brand] || []) : (facets.series || [])).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.category || "__all"} onValueChange={(v) => setFilters({ ...filters, category: v === "__all" ? "" : v })}>
              <SelectTrigger className="sm:w-48 h-10" data-testid="list-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Tüm Kategoriler</SelectItem>
                {(filters.brand ? (facets.categories_by_brand?.[filters.brand] || []) : (facets.categories || [])).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="sm:w-56 h-10" data-testid="list-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En Son Eklenen</SelectItem>
                <SelectItem value="oldest">En Eski Eklenen</SelectItem>
                <SelectItem value="qty_desc">Stok: Çoktan Aza</SelectItem>
                <SelectItem value="qty_asc">Stok: Azdan Çoğa</SelectItem>
                <SelectItem value="name">İsme Göre (A-Z)</SelectItem>
                {isAdmin && <SelectItem value="value_desc">Stok Değeri: Yüksekten Düşüğe</SelectItem>}
                {isAdmin && <SelectItem value="price_desc">Fiyat: Yüksekten Düşüğe</SelectItem>}
                {isAdmin && <SelectItem value="price_asc">Fiyat: Düşükten Yükseğe</SelectItem>}
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-10" onClick={() => loadBrowse(1)} data-testid="list-filter-btn"><Filter className="h-4 w-4 mr-2" />Filtrele</Button>
            <Button variant="secondary" className="h-10" onClick={() => { setScanTarget("edit"); setScanOpen(true); }} data-testid="catalog-camera-scan-edit-btn"><Camera className="h-4 w-4 mr-2" />Kamera ile Tara & Düzenle</Button>
            {(filters.q || filters.brand || filters.category) && <Button variant="ghost" className="h-10" onClick={() => setFilters({ q: "", brand: "", category: "" })}><X className="h-4 w-4 mr-1" />Temizle</Button>}
          </div>

          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm text-muted-foreground" data-testid="list-count">Toplam <b className="text-foreground">{NUM(browse.total)}</b> ürün · Sayfa {browse.page}/{browse.pages}</div>
            {isAdmin && selected.length > 0 && (
              <div className="flex items-center gap-2 bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-1.5" data-testid="bulk-bar">
                <span className="text-sm font-semibold text-destructive">{selected.length} ürün seçildi</span>
                <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 className="h-4 w-4 mr-1.5" />Seçilenleri Sil</Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Temizle</Button>
              </div>
            )}
          </div>

          <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-[#0B192C] hover:bg-[#0B192C] border-0">
                  <TableHead className="w-1 p-0" />
                  {isAdmin && <TableHead className="w-10"><Checkbox className="border-white/40 data-[state=checked]:bg-[#EA5820] data-[state=checked]:border-[#EA5820]" checked={browse.items.length > 0 && selected.length === browse.items.length} onCheckedChange={toggleAll} data-testid="select-all" /></TableHead>}
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-white/80">Görsel & Kod</TableHead>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-white/80">Ürün Adı & Marka</TableHead>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-white/80">Kategori</TableHead>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-right text-white/80">Mevcut Stok</TableHead>
                  {isAdmin && <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-right text-white/80">Birim Maliyet</TableHead>}
                  {isAdmin && <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-right text-white/80">Satış Fiyatı</TableHead>}
                  {isAdmin && <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-right text-white/80">Kâr Marjı</TableHead>}
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-white/80">Durum</TableHead>
                  <TableHead className="text-[11px] font-bold uppercase tracking-wider font-head text-right text-white/80">İşlemler</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {browse.items.map((p) => {
                    const crit = p.critical_level > 0 && p.quantity <= p.critical_level;
                    const out = (p.quantity || 0) <= 0;
                    const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
                    const sale = p.sale_price != null ? p.sale_price : p.price;
                    const margin = isAdmin && sale > 0 && p.cost != null ? Math.round(((sale - p.cost) / sale) * 100) : null;
                    return (
                      <TableRow key={p.id} className={cn("group", out && "bg-red-50/30", crit && !out && "bg-amber-50/30")} data-testid={`list-row-${p.id}`}>
                        <TableCell className="p-0 w-1"><span className={cn("block w-1 h-12 rounded-r", out ? "bg-red-500" : crit ? "bg-amber-500" : "bg-emerald-500")} /></TableCell>
                        {isAdmin && <TableCell><Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`select-${p.id}`} /></TableCell>}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {imgs[0] ? <img src={fileUrl(imgs[0])} alt="" loading="lazy" decoding="async" className="h-10 w-10 rounded-md object-contain bg-white border" />
                              : <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center"><Package className="h-4 w-4 text-muted-foreground/40" /></div>}
                            {p.code && <span className="font-mono text-[11px] text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{p.code}</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <button type="button" onClick={() => openDetail(p)} className="text-left font-semibold text-sm leading-tight flex items-center gap-1.5 hover:text-primary transition-colors" data-testid={`list-detail-${p.id}`}>
                            {pname(p)}
                          </button>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {p.brand && <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 text-secondary px-2 py-px text-[10px] font-semibold"><Tag className="h-2.5 w-2.5" />{p.brand}</span>}
                            {(p.series || (p.discount_group && p.discount_group !== p.brand)) && <span className="text-[10px] text-muted-foreground">{p.series || p.discount_group}</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.category || "—"}</TableCell>
                        <TableCell className="text-right"><span className={cn("tnum text-sm font-bold", out ? "text-red-600" : crit ? "text-amber-600" : "")}>{NUM(p.quantity)}</span> <span className="text-[11px] text-muted-foreground">{p.unit}</span><div className="mt-0.5 flex justify-end"><StockSplit p={p} testid={`list-stock-${p.id}`} /></div>{p.critical_level > 0 && <div className="text-[10px] text-muted-foreground tnum">krit. {NUM(p.critical_level)}</div>}</TableCell>
                        {isAdmin && <TableCell className="text-right tnum text-sm text-slate-600">{p.cost != null ? TRY(p.cost) : "—"}</TableCell>}
                        {isAdmin && <TableCell className="text-right tnum text-sm font-bold">{TRY(sale)}{p.sale_price != null && p.sale_price !== p.price && <span className="block text-[10px] text-muted-foreground font-normal">Liste: {TRY(p.price)}</span>}{p.currency && p.currency !== "TL" && <span className="block text-[10px] text-muted-foreground font-normal">{NUM(p.list_orig ?? p.list_price)} {p.currency}</span>}</TableCell>}
                        {isAdmin && <TableCell className="text-right">{margin !== null ? <span className={cn("tnum text-xs font-bold rounded-full px-2 py-0.5", margin >= 20 ? "bg-emerald-50 text-emerald-700" : margin >= 0 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>%{margin}</span> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>}
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {out ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0 text-[9px] px-1.5">Tükendi</Badge> : crit ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[9px] px-1.5 gap-0.5"><AlertTriangle className="h-2.5 w-2.5" />Kritik</Badge> : <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0 text-[9px] px-1.5">Normal</Badge>}
                            {requestedIds.includes(p.id) && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[9px] gap-0.5 px-1.5" data-testid={`requested-${p.id}`}><Clock className="h-2.5 w-2.5" />Talepte</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-0.5">
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Hızlı Giriş (+)" onClick={() => { setMove({ type: "giris", quantity: 0, note: "" }); setMoveDlg(p); }} data-testid={`list-move-${p.id}`}><Plus className="h-4 w-4 text-emerald-600" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Hızlı Çıkış (−)" onClick={() => { setMove({ type: "cikis", quantity: 0, note: "" }); setMoveDlg(p); }} data-testid={`list-move-out-${p.id}`}><ClipboardEdit className="h-4 w-4 text-secondary" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Detay" onClick={() => openDetail(p)} data-testid={`list-view-${p.id}`}><Eye className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Düzenle" onClick={() => openEdit(p)} data-testid={`list-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                            {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8" title="Sil" onClick={() => del(p.id)} data-testid={`list-del-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {browseLoading && <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
            {!browseLoading && browse.items.length === 0 && <div className="p-6"><Empty title="Aramanıza Uygun Ürün Bulunamadı" desc="Filtreleri sıfırlayarak veya yeni ürün ekleyerek devam edebilirsiniz." action={<Button size="sm" variant="outline" onClick={() => setFilters({ q: "", brand: "", category: "", discount_group: "" })} data-testid="stock-empty-reset-filter-btn"><X className="h-4 w-4 mr-1.5" />Filtreleri Sıfırla</Button>} /></div>}
          </div>

          {browse.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" disabled={browse.page <= 1} onClick={() => loadBrowse(browse.page - 1)} data-testid="list-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-muted-foreground tabular-nums">Sayfa {browse.page} / {browse.pages}</span>
              <Button size="sm" variant="outline" disabled={browse.page >= browse.pages} onClick={() => loadBrowse(browse.page + 1)} data-testid="list-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      )}

      {tab === "fiyatsiz" && isAdmin && (
        <div className="space-y-3" data-testid="stock-noprice-panel">
          <div className="rounded-xl border bg-amber-50/60 border-amber-200 px-4 py-3 flex items-start gap-2 text-sm text-amber-800">
            <Coins className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Liste fiyatı ve manuel fiyatı olmayan ürünler. Bir ürüne tıklayıp <b>Genel / Elektrikçi Faturalı / Elektrikçi KDV'siz</b> satış fiyatını ve <b>maliyeti</b> girin; <b>kâr marjını</b> anında görün. Satış fiyatı girilince ürün listeden düşer.</span>
          </div>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Ürün adı / marka / kod ara" value={npQuery} onChange={(e) => setNpQuery(e.target.value)} className="pl-9 h-9" data-testid="noprice-search" />
          </div>
          {noPrice.loading ? (
            <div className="py-12 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
          ) : (() => {
            const q = npQuery.trim().toLocaleLowerCase("tr");
            const items = q ? noPrice.items.filter((p) => [p.name, p.brand, p.series, p.code, p.barcode].some((v) => String(v || "").toLocaleLowerCase("tr").includes(q))) : noPrice.items;
            if (noPrice.items.length === 0) return <Empty title="Fiyatsız ürün yok" desc="Tüm ürünlerin liste ya da manuel fiyatı tanımlı." />;
            if (items.length === 0) return <Empty title="Sonuç yok" desc="Aramanıza uygun fiyatsız ürün bulunamadı." />;
            return (
              <div className="rounded-xl border overflow-hidden">
                <div className="px-4 py-2 bg-muted/40 text-xs font-semibold text-muted-foreground" data-testid="noprice-count">{items.length} / {noPrice.items.length} fiyatsız ürün</div>
                <div className="divide-y">
                  {items.map((p) => {
                    const open = npExpand === p.id;
                    const kdv = Number(p.kdv_rate) || 20;
                    const gInc = Number(npForm.price_general) || 0;
                    const gNet = gInc > 0 ? gInc / (1 + kdv / 100) : 0;
                    const cost = npForm.cost_manual !== "" && npForm.cost_manual != null ? Number(npForm.cost_manual) : (Number(p.cost) || 0);
                    const margin = gNet > 0 ? Math.round(((gNet - cost) / gNet) * 100) : null;
                    return (
                      <div key={p.id} data-testid={`noprice-row-${p.id}`}>
                        <button type="button" onClick={() => openNpRow(p)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors" data-testid={`noprice-toggle-${p.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">{p.name}</div>
                            <div className="text-[11px] text-muted-foreground">{[p.brand, p.series].filter(Boolean).join(" · ") || "—"}{p.code ? ` · ${p.code}` : ""}</div>
                          </div>
                          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 shrink-0">Fiyatsız</span>
                          <ChevronRight className={cn("h-4 w-4 text-muted-foreground shrink-0 transition-transform", open && "rotate-90")} />
                        </button>
                        {open && (
                          <div className="px-4 pb-4 pt-1 bg-muted/20 space-y-3" data-testid={`noprice-editor-${p.id}`}>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                              <div><Label className="text-[11px] text-muted-foreground">Genel Satış (KDV dahil)</Label><Input type="number" className="mt-1 tabular-nums" value={npForm.price_general} onChange={(e) => setNpForm({ ...npForm, price_general: e.target.value })} data-testid={`noprice-general-${p.id}`} autoFocus /></div>
                              <div><Label className="text-[11px] text-emerald-700">Elektrikçi Faturalı (KDV dahil)</Label><Input type="number" className="mt-1 tabular-nums" value={npForm.price_elec_vat} onChange={(e) => setNpForm({ ...npForm, price_elec_vat: e.target.value })} data-testid={`noprice-elec-${p.id}`} /></div>
                              <div><Label className="text-[11px] text-amber-700">Elektrikçi KDV'siz (net)</Label><Input type="number" className="mt-1 tabular-nums" value={npForm.price_elec_novat} onChange={(e) => setNpForm({ ...npForm, price_elec_novat: e.target.value })} data-testid={`noprice-novat-${p.id}`} /></div>
                              <div><Label className="text-[11px] text-muted-foreground">Maliyet (KDV hariç)</Label><Input type="number" className="mt-1 tabular-nums" value={npForm.cost_manual} onChange={(e) => setNpForm({ ...npForm, cost_manual: e.target.value })} data-testid={`noprice-cost-${p.id}`} /></div>
                            </div>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                              <div className="flex items-center gap-4 text-sm">
                                <div><span className="text-[11px] text-muted-foreground">Genel Net: </span><b className="tabular-nums">{TRY(gNet)}</b></div>
                                <div><span className="text-[11px] text-muted-foreground">Maliyet: </span><b className="tabular-nums">{TRY(cost)}</b></div>
                                <div className={cn("font-bold", margin == null ? "text-muted-foreground" : margin >= 0 ? "text-emerald-600" : "text-destructive")} data-testid={`noprice-margin-${p.id}`}><span className="text-[11px] font-normal text-muted-foreground">Kâr Marjı: </span>{margin == null ? "—" : `%${margin}`}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`noprice-detail-${p.id}`}><Eye className="h-4 w-4 mr-1.5" />Tam Detay</Button>
                                <Button size="sm" onClick={() => saveNoPrice(p)} data-testid={`noprice-save-${p.id}`}><CheckCircle2 className="h-4 w-4 mr-1.5" />Kaydet</Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <Dialog open={seriesDiscOpen} onOpenChange={setSeriesDiscOpen}>
        <DialogContent className="max-w-md" data-testid="series-disc-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><BadgePercent className="h-5 w-5 text-primary" />"{(form.series || "").trim()}" serisine iskonto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Bu seriye (markadan bağımsız) iskonto tanımlanır. Maliyet iskontosu gruptaki ürünlerin maliyetini yeniden hesaplar.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">Maliyet İsk. (%)</Label><Input type="number" className="mt-1" value={seriesDiscForm.cost_discount} onChange={(e) => setSeriesDiscForm({ ...seriesDiscForm, cost_discount: e.target.value })} data-testid="series-disc-cost" autoFocus /></div>
              <div><Label className="text-xs text-muted-foreground">Genel Satış İsk. (%)</Label><Input type="number" className="mt-1" value={seriesDiscForm.sale_discount} onChange={(e) => setSeriesDiscForm({ ...seriesDiscForm, sale_discount: e.target.value })} data-testid="series-disc-sale" /></div>
              <div><Label className="text-xs text-emerald-700">Faturalı Elek. (%)</Label><Input type="number" className="mt-1" value={seriesDiscForm.electrician_discount} onChange={(e) => setSeriesDiscForm({ ...seriesDiscForm, electrician_discount: e.target.value })} data-testid="series-disc-elec" /></div>
              <div><Label className="text-xs text-amber-700">Faturasız Elek. (%)</Label><Input type="number" className="mt-1" value={seriesDiscForm.electrician_nobill_discount} onChange={(e) => setSeriesDiscForm({ ...seriesDiscForm, electrician_nobill_discount: e.target.value })} data-testid="series-disc-elec-nb" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeriesDiscOpen(false)} data-testid="series-disc-cancel">Vazgeç</Button>
            <Button onClick={saveSeriesDisc} data-testid="series-disc-save"><CheckCircle2 className="h-4 w-4 mr-1.5" />Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tab === "hareketler" && isAdmin && (
        <div className="space-y-4" data-testid="stock-moves-panel">
          {approvalCount > 0 && (
            <button onClick={() => setTab("onay")} className="w-full text-left rounded-xl border-2 border-amber-300 bg-amber-50/70 px-4 py-3 flex items-center gap-2 font-head font-black text-sm text-amber-900 hover:bg-amber-100/70 transition-colors" data-testid="pending-stock-box">
              <AlertTriangle className="h-5 w-5 text-amber-600" />Onay bekleyen {approvalCount} alış faturası var<span className="text-xs font-normal text-amber-800 ml-1">— "Onay Bekleyenler" sekmesinde eşleştirip stoğa işleyin →</span>
            </button>
          )}
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mr-1">Filtre:</span>
              {[["", "Tümü"], ["giris", "Giriş"], ["cikis", "Çıkış"], ["sayim", "Sayım"], ["olusturma", "Yeni Ürün"], ["silme", "Silinen"], ["duzeltme", "Düzeltme"]].map(([val, lbl]) => (
                <button key={val || "all"} onClick={() => setMoveTypeFilter(val)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${moveTypeFilter === val ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:text-foreground"}`} data-testid={`moves-filter-${val || "all"}`}>{lbl}</button>
              ))}
              <Button size="sm" variant="ghost" className="ml-auto" onClick={loadMoves} data-testid="moves-refresh"><History className="h-4 w-4 mr-1" />Yenile</Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mr-1">Tarih:</span>
              <div className="w-40"><DatePicker value={moveStart} onChange={setMoveStart} placeholder="Başlangıç" data-testid="moves-start" /></div>
              <span className="text-muted-foreground">–</span>
              <div className="w-40"><DatePicker value={moveEnd} onChange={setMoveEnd} placeholder="Bitiş" data-testid="moves-end" /></div>
              {(moveStart || moveEnd) && <Button size="sm" variant="ghost" onClick={() => { setMoveStart(""); setMoveEnd(""); }} data-testid="moves-date-clear"><X className="h-4 w-4 mr-1" />Temizle</Button>}
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={exportMovesExcel} data-testid="moves-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
                <Button size="sm" variant="outline" onClick={exportMovesPdf} data-testid="moves-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
              </div>
            </div>
          </div>
          {movesLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : moves.length === 0 ? (
            <Empty title="Hareket yok" desc="Henüz stokla ilgili bir işlem kaydedilmedi." />
          ) : (
            <div className="space-y-1.5" data-testid="stock-moves-stream">
              {moves.map((m) => {
                const meta = moveMeta(m.type);
                const delta = (m.new_qty ?? 0) - (m.old_qty ?? 0);
                return (
                  <div key={m.id} className={cn("relative flex items-center gap-3 pl-4 pr-3 py-2.5 border-l-4 rounded-r-xl rounded-l-md bg-card border-y border-r border-slate-200/70 hover:bg-slate-50 transition-colors", meta.stripe, m.undone && "opacity-60", m.below_critical && "border-red-300 bg-red-50/50")} data-testid={`move-row-${m.id}`}>
                    {m.image_url ? <img src={fileUrl(m.image_url)} alt="" loading="lazy" decoding="async" className="h-14 w-14 rounded-lg object-contain bg-white border shrink-0" />
                      : <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center shrink-0"><Package className="h-6 w-6 text-muted-foreground" /></div>}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={cn(meta.cls, "text-[10px] uppercase tracking-wide font-head")}>{meta.label}</Badge>
                        <span className="font-semibold text-sm truncate">{m.product_name}</span>
                        {!m.product_exists && m.type !== "silme" && <span className="text-[10px] text-muted-foreground">(ürün silinmiş)</span>}
                        {m.below_critical && <Badge variant="destructive" className="gap-1 text-[10px]" data-testid={`move-critical-${m.id}`}><AlertTriangle className="h-3 w-3" />Kritik seviye altında</Badge>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-1.5">
                        <Cell label="Miktar"><span className="tnum text-sm font-semibold">{NUM(m.old_qty)} <ArrowRight className="inline h-3 w-3 text-muted-foreground" /> {NUM(m.new_qty)} <span className="text-[10px] text-muted-foreground font-normal">{m.unit || ""}</span></span></Cell>
                        <Cell label="Değişim"><span className={cn("tnum text-sm font-black", delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-muted-foreground")}>{delta > 0 ? "+" : ""}{NUM(delta)}</span></Cell>
                        <Cell label="Kullanıcı"><span className="text-xs font-medium inline-flex items-center gap-1 truncate"><User className="h-3 w-3 text-muted-foreground" />{m.user_name || "—"}</span></Cell>
                        <Cell label="Tarih"><span className="text-xs tnum">{fmtDT(m.created_at)}</span></Cell>
                      </div>
                      {m.source === "invoice" && <div className="text-[11px] mt-1 inline-flex items-center gap-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 px-1.5 py-0.5 font-semibold" data-testid={`move-invoice-src-${m.id}`}><Building2 className="h-3 w-3" />{m.supplier}{m.invoice_no ? ` · Fatura ${m.invoice_no}` : ""}</div>}
                      {m.note && <div className="text-[11px] text-muted-foreground italic mt-1">{m.note}</div>}
                      {m.undone && <div className="text-[11px] text-amber-600 font-medium mt-0.5">Geri alındı ({m.undone_by})</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isAdmin && m.below_critical && m.product_exists && (
                        requestedIds.includes(m.product_id)
                          ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 gap-1" data-testid={`move-requested-${m.id}`}><Clock className="h-3 w-3" />Talepte</Badge>
                          : <Button size="sm" className="bg-destructive hover:bg-destructive/90" onClick={() => orderFromMove(m)} data-testid={`move-order-${m.id}`}><ShoppingCart className="h-4 w-4 mr-1" />Sipariş Talebi</Button>
                      )}
                      {isAdmin && ["giris", "cikis", "sayim"].includes(m.type) && !m.undone && m.product_exists && (
                        <Button size="sm" variant="outline" onClick={() => undoMove(m)} data-testid={`move-undo-${m.id}`}><RotateCcw className="h-4 w-4 mr-1" />Geri Al</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Product dialog */}
      <Dialog open={dlg} onOpenChange={tryCloseDlg}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{editId ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fotoğraflar</Label>
              <div className="flex flex-wrap gap-3">
                {(form.images || []).map((img, idx) => (
                  <div key={idx} className="relative h-20 w-20 group">
                    <img src={fileUrl(img)} alt="" className="h-20 w-20 rounded-lg object-contain bg-white border" />
                    {idx === 0 && <span className="absolute bottom-0 inset-x-0 bg-primary text-white text-[9px] text-center rounded-b-lg">KAPAK</span>}
                    <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-white flex items-center justify-center" data-testid={`img-remove-${idx}`}><X className="h-3 w-3" /></button>
                  </div>
                ))}
                <label className="cursor-pointer h-20 w-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary">
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onImage} data-testid="p-image" />
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  <span className="text-[10px] mt-1">Galeri</span>
                </label>
                <label className="cursor-pointer h-20 w-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary">
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onImage} data-testid="p-image-camera" />
                  <Camera className="h-5 w-5" />
                  <span className="text-[10px] mt-1">Kamera</span>
                </label>
              </div>
              {editId && (
                <Button type="button" size="sm" variant="outline" onClick={genAiImage} disabled={aiLoading} data-testid="ai-image-btn">
                  {aiLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5 text-primary" />}AI Görsel Üret
                </Button>
              )}
            </div>
            <Field className="col-span-2" label="Ürün Adı *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="p-name" /></Field>
            <Field label="Marka">
              {(form._newBrand || (form.brand && !facets.brands.includes(form.brand))) ? (
                <div className="flex gap-1">
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="p-brand" placeholder="Yeni marka adı" />
                  {facets.brands.length > 0 && <Button type="button" variant="ghost" size="icon" onClick={() => setForm({ ...form, _newBrand: false, brand: "" })}><X className="h-4 w-4" /></Button>}
                </div>
              ) : (
                <Select value={form.brand || ""} onValueChange={(v) => v === "__new" ? setForm({ ...form, _newBrand: true, brand: "" }) : setForm({ ...form, brand: v })}>
                  <SelectTrigger data-testid="p-brand"><SelectValue placeholder="Marka seçin" /></SelectTrigger>
                  <SelectContent>
                    {facets.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    <SelectItem value="__new">+ Yeni Marka Ekle</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Seri (Seri Adı)">
              <Input value={form.series || ""} onChange={(e) => setForm({ ...form, series: e.target.value })} data-testid="p-series" placeholder="ör. NOVELLA ANTRASİT / TTR" list="p-series-list" />
              <datalist id="p-series-list">
                {(((facets.series_by_brand && facets.series_by_brand[form.brand]) || facets.series) || []).map((s) => <option key={s} value={s} />)}
              </datalist>
              {(form.series || "").trim() && (() => {
                const key = discountKeys(form).find((k) => discMap[k]);
                const d = key ? discMap[key] : null;
                return d
                  ? <p className="text-[11px] text-emerald-700 mt-1 font-semibold" data-testid="p-series-disc-hint">✓ "{form.series.trim()}" serisi iskontosu: maliyet %{d.cost} · satış %{d.sale}</p>
                  : <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid="p-series-disc-hint"><span className="text-[11px] text-amber-600">Bu seri için iskonto tanımlı değil.</span><Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => { setSeriesDiscForm({ cost_discount: "", sale_discount: "", electrician_discount: "", electrician_nobill_discount: "" }); setSeriesDiscOpen(true); }} data-testid="p-series-disc-add"><BadgePercent className="h-3 w-3 mr-1" />Bu seriye iskonto tanımla</Button></div>;
              })()}
            </Field>
            <Field label="Ürün Kodu"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="p-code" /></Field>
            <Field label="Barkod">
              <div className="flex gap-1">
                <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="p-barcode" />
                <Button type="button" variant="outline" size="icon" className="shrink-0" title="Kameradan/galeriden barkod oku" onClick={() => { setScanTarget("barcode"); setScanOpen(true); }} data-testid="p-barcode-scan"><ScanLine className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field className="col-span-2" label="Ek Barkodlar (opsiyonel)">
              <div className="space-y-2">
                {(form.barcodes || []).map((b, i) => (
                  <div key={i} className="flex gap-1">
                    <Input value={b} onChange={(e) => setBarcodeAt(i, e.target.value)} placeholder={`Ek barkod ${i + 1}`} data-testid={`p-extra-barcode-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={() => rmBarcode(i)} data-testid={`p-extra-barcode-rm-${i}`}><X className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={addBarcode} data-testid="p-add-barcode"><Plus className="h-4 w-4 mr-1" />Barkod Ekle</Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => { setScanTarget("extra-barcode"); setScanOpen(true); }} data-testid="p-extra-barcode-scan"><ScanLine className="h-4 w-4 mr-1" />Tarayarak Ekle</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Bir ürünün birden fazla barkodu olabilir; hepsiyle aranıp taranabilir.</p>
              </div>
            </Field>
            <Field label="Kategori / Grup"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="p-cat" /></Field>
            <Field label="Birim">
              <Select value={UNITS.includes((form.unit || "").toUpperCase()) ? form.unit.toUpperCase() : "__custom"} onValueChange={(v) => v !== "__custom" && setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="p-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  <SelectItem value="__custom">Diğer (elle)</SelectItem>
                </SelectContent>
              </Select>
              {!UNITS.includes((form.unit || "").toUpperCase()) && (
                <Input className="mt-2" placeholder="BİRİM" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value.toUpperCase() })} data-testid="p-unit-custom" />
              )}
            </Field>
            <Field label="Depo Stok"><Input type="number" inputMode="decimal" value={form.qty_depo} onChange={(e) => setForm({ ...form, qty_depo: e.target.value })} data-testid="p-qty-depo" /></Field>
            <Field label="Satış Mağazası Stok"><Input type="number" inputMode="decimal" min="0" value={form.qty_magaza} onChange={(e) => setForm({ ...form, qty_magaza: e.target.value })} data-testid="p-qty-magaza" /></Field>
            <Field label="Toplam Stok">
              <div className="h-10 px-3 rounded-md border bg-muted/40 flex items-center tnum font-bold" data-testid="p-qty-total">{NUM(Math.round(((Number(form.qty_depo) || 0) + (Number(form.qty_magaza) || 0)) * 100) / 100)}<span className="ml-1.5 text-xs font-semibold text-muted-foreground">{form.unit}</span></div>
              {editId && <p className="text-[10px] text-muted-foreground mt-1">Adet değişikliği bölüm bazlı sayım hareketi olarak kaydedilir.</p>}
            </Field>
            <Field label="Kritik Seviye"><Input type="number" value={form.critical_level} onChange={(e) => setForm({ ...form, critical_level: e.target.value })} data-testid="p-crit" /></Field>
            {isAdmin && (
              <>
                <Field label="Döviz Tipi">
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="p-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TL">TL</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={`Liste Fiyatı (${form.currency})`}><Input type="number" value={form.list_price} onChange={(e) => setForm({ ...form, list_price: e.target.value })} data-testid="p-list-price" /></Field>
                <Field label="Fiyat KDV Durumu">
                  <Select value={form.price_kdv_included ? "dahil" : "haric"} onValueChange={(v) => setForm({ ...form, price_kdv_included: v === "dahil" })}>
                    <SelectTrigger data-testid="p-kdv-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dahil">KDV Dahil</SelectItem>
                      <SelectItem value="haric">+KDV (Hariç)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="KDV Oranı (%)"><Input type="number" value={form.kdv_rate} onChange={(e) => setForm({ ...form, kdv_rate: e.target.value })} data-testid="p-kdv-rate" /></Field>
                <div className="col-span-2 rounded-lg border border-secondary/30 bg-secondary/5 p-3 space-y-2" data-testid="manual-pricing">
                  <div className="text-xs font-bold uppercase tracking-wider text-secondary">Manuel Fiyatlar · doluysa iskontoyu ezer (KDV DAHİL)</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Field label="Genel Satış"><Input type="number" value={form.price_general} onChange={(e) => setForm({ ...form, price_general: e.target.value })} placeholder="otomatik" data-testid="p-price-general" /></Field>
                    <Field label="Elektrikçi Faturalı"><Input type="number" value={form.price_elec_vat} onChange={(e) => setForm({ ...form, price_elec_vat: e.target.value })} placeholder="otomatik" data-testid="p-price-elec-vat" /></Field>
                    <Field label="Elektrikçi KDV'siz"><Input type="number" value={form.price_elec_novat} onChange={(e) => setForm({ ...form, price_elec_novat: e.target.value })} placeholder="otomatik" data-testid="p-price-elec-novat" /></Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Field label="Manuel Maliyet (KDV hariç)"><Input type="number" value={form.cost_manual} onChange={(e) => setForm({ ...form, cost_manual: e.target.value })} placeholder="iskontodan otomatik" data-testid="p-cost-manual" /></Field>
                    <div className="rounded-md bg-card border p-2 flex flex-col justify-center">
                      <div className="text-[10px] text-muted-foreground uppercase">KDV Dahil Maliyet</div>
                      <div className="font-head font-black tabular-nums text-sm" data-testid="p-cost-incl">{(() => { const cb = (form.cost_manual !== "" && form.cost_manual != null) ? Number(form.cost_manual) : pricePreview().cost; return TRY(cb * (1 + (Number(form.kdv_rate) || 0) / 100)); })()}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">TL Fiyatı · KDV hariç net (kur: {form.currency === "TL" ? "1" : rates[form.currency]})</span>
                  <span className="font-head font-black tabular-nums text-primary" data-testid="p-tl-price">{TRY(computedPrice())}</span>
                </div>
                {(() => { const pv = pricePreview(); return (
                  <div className="col-span-2 rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2" data-testid="price-preview">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fiyat Önizleme{pv.g ? ` · ${pv.g}` : ""}</span>
                      {!pv.hasDisc && <span className="text-[10px] text-amber-600">Bu grup için iskonto tanımlı değil</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="rounded-md bg-card p-2 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Maliyet{pv.d.cost ? ` (-%${pv.d.cost})` : ""}</div>
                        <div className="font-head font-black tabular-nums text-sm" data-testid="preview-cost">{TRY(pv.cost)}</div>
                      </div>
                      <div className="rounded-md bg-card p-2 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Satış{pv.d.sale ? ` (-%${pv.d.sale})` : ""}</div>
                        <div className="font-head font-black tabular-nums text-sm text-primary" data-testid="preview-sale">{TRY(pv.sale)}</div>
                      </div>
                      <div className="rounded-md bg-card p-2 text-center">
                        <div className="text-[10px] text-muted-foreground uppercase">Kâr · %{pv.margin}</div>
                        <div className={`font-head font-black tabular-nums text-sm ${pv.profit >= 0 ? "text-[#16A34A]" : "text-destructive"}`} data-testid="preview-profit">{TRY(pv.profit)}</div>
                      </div>
                    </div>
                  </div>
                ); })()}
              </>
            )}
          </div>
          {isAdmin && editId && isCableProduct(form) && (
            <div className="mt-2">
              <DrumManager product={{ id: editId, drums: form.drums || [], unit: form.unit }} isAdmin={isAdmin} onChanged={(up) => setForm((f) => ({ ...f, drums: up.drums || [] }))} />
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-between">
            {isAdmin && editId ? <Button type="button" variant="ghost" size="sm" onClick={() => setHistOpen(true)} data-testid="product-history-btn"><History className="h-4 w-4 mr-1.5" />Değişiklik Geçmişi</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => tryCloseDlg(false)}>İptal</Button>
              <Button onClick={save} data-testid="save-product">{editId ? "Güncelle" : "Ekle"}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ürün değişiklik geçmişi */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="product-history-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><History className="h-5 w-5 text-primary" />Değişiklik Geçmişi</DialogTitle></DialogHeader>
          {editId ? <RecordHistory entityType="product" entityId={editId} /> : null}
        </DialogContent>
      </Dialog>

      {/* Movement dialog */}
      <Dialog open={!!moveDlg} onOpenChange={(o) => !o && setMoveDlg(null)}>
        <DialogContent className="max-w-md" data-testid="move-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Stok Hareketi</DialogTitle></DialogHeader>
          {moveDlg && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-md p-3">
                <div className="font-semibold">{moveDlg.name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap mt-1">Mevcut: <StockSplit p={moveDlg} total testid="move-current" /></div>
              </div>
              <Field label="İşlem Türü">
                <Select value={move.type} onValueChange={(v) => setMove({ ...move, type: v, location: v === "transfer" ? "magaza" : move.type === "transfer" ? "depo" : move.location })}>
                  <SelectTrigger data-testid="move-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="giris">Stok Girişi (+)</SelectItem>
                    <SelectItem value="cikis">Stok Çıkışı (−)</SelectItem>
                    <SelectItem value="sayim">Sayım (mutlak değer)</SelectItem>
                    <SelectItem value="transfer">Aktarma (Depo ↔ Mağaza)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={move.type === "transfer" ? "Aktarma Yönü" : "Bölüm"}>
                <div className="grid grid-cols-2 gap-2" data-testid="move-loc">
                  {(move.type === "transfer" ? [["magaza", "Depo → Mağaza"], ["depo", "Mağaza → Depo"]] : [["depo", "Depo"], ["magaza", "Satış Mağazası"]]).map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setMove({ ...move, location: v })} className={cn("rounded-lg border px-3 py-2 text-sm font-bold transition-colors", (move.location || "depo") === v ? "border-primary bg-primary/5 ring-1 ring-primary text-foreground" : "hover:bg-muted/50 text-muted-foreground")} data-testid={`move-loc-${v}`}>{l}</button>
                  ))}
                </div>
              </Field>
              <Field label={move.type === "sayim" ? "Sayılan Miktar" : "Miktar"}>
                <Input type="number" value={move.quantity} onChange={(e) => setMove({ ...move, quantity: e.target.value })} data-testid="move-qty" />
              </Field>
              <p className="text-xs text-muted-foreground">Giriş/sayım sonrası kritik seviye otomatik olarak yeni miktarın %25'i olur.</p>
              <Field label="Not"><Input value={move.note} onChange={(e) => setMove({ ...move, note: e.target.value })} data-testid="move-note" /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDlg(null)}>İptal</Button>
            <Button onClick={saveMove} data-testid="save-move">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Döviz kuru dialog */}
      <Dialog open={kurDlg} onOpenChange={setKurDlg}>
        <DialogContent className="max-w-sm" data-testid="kur-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Döviz Kuru</DialogTitle></DialogHeader>
          <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1 -mt-1" data-testid="kur-auto-info">
            <div className="font-bold text-foreground flex items-center gap-1.5"><span className={cn("h-2 w-2 rounded-full", rateStatusOf(rates).ok ? "bg-emerald-500" : "bg-amber-500")} />{rateStatusOf(rates).label}</div>
            <div className="text-muted-foreground">TCMB her iş günü 15:30'da kurları ilan eder; sistem 15:40'ta <b>döviz satış kurunu</b> otomatik uygular ve döviz cinsindeki ürünlerin TL fiyatını yeniler (gecikmeye karşı 16:40'ta tekrar kontrol eder).</div>
            <div>Son güncelleme: <b data-testid="kur-last-update">{rateWhen(rates)}</b></div>
            {rates.last_check_ok === false && <div className="text-destructive" data-testid="kur-last-error">Son kontrol başarısız: {rates.last_check_error}</div>}
          </div>
          <Button type="button" onClick={applyTcmbNow} disabled={kurBusy} data-testid="kur-tcmb-apply-btn">{kurBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}TCMB'den Şimdi Güncelle ve Uygula</Button>
          <p className="text-sm text-muted-foreground">Elle kur girmek için: kaydedince döviz cinsindeki tüm ürünlerin TL fiyatı yeniden hesaplanır.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="USD → TL"><Input type="number" step="any" value={kurForm.USD} onChange={(e) => setKurForm({ ...kurForm, USD: e.target.value })} data-testid="kur-usd" /></Field>
            <Field label="EUR → TL"><Input type="number" step="any" value={kurForm.EUR} onChange={(e) => setKurForm({ ...kurForm, EUR: e.target.value })} data-testid="kur-eur" /></Field>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={fetchTcmbKur} data-testid="kur-tcmb-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />TCMB Güncel Kuru Çek (döviz satış)</Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKurDlg(false)}>İptal</Button>
            <Button variant="secondary" onClick={saveKur} data-testid="save-kur">Elle Kaydet ve Uygula</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductImportDialog open={!!importPreview} data={importPreview} onClose={() => setImportPreview(null)} onDone={() => { loadStats(); loadFacets(); loadRates(); if (tab === "liste") loadBrowse(1); }} />

      {/* Ürün detay modalı */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="product-detail-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Ürün Detayı</DialogTitle></DialogHeader>
          {detailLoading && <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {detail && <ProductDetailView data={detail} isAdmin={isAdmin} loading={detailLoading}
            onMove={(p) => { setDetail(null); setMove({ type: "giris", quantity: 0, note: "" }); setMoveDlg(p); }}
            onEdit={(p) => { setDetail(null); openEdit(p); }}
            onDrumChanged={(up) => { setDetail((d) => (d ? { ...d, product: up } : d)); loadStats(); }}
            onDelete={(id) => { setDetail(null); del(id); }} onQuote={addToQuote} />}
        </DialogContent>
      </Dialog>

      <QuoteEditorDialog open={!!quoteInit} onOpenChange={(o) => !o && setQuoteInit(null)} initial={quoteInit} contacts={qContacts} onSaved={onQuoteSaved} onContactsChanged={loadQContacts} />

      <QuickCountDialog open={quickOpen} onOpenChange={setQuickOpen} onAddNew={(code) => { setQuickOpen(false); openNew(code); }}
        onApplied={() => { loadStats(); if (tab === "hareketler") loadMoves(); else if (tab === "liste") loadBrowse(browse.page); else if (q.trim()) search(); }} />
      <StockReportDialog open={reportOpen} onOpenChange={setReportOpen} />

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />
    </div>
  );
}

function DrumManager({ product, isAdmin, onChanged }) {
  const [drums, setDrums] = useState(product.drums || []);
  const [addForm, setAddForm] = useState({ label: "", length: "", note: "", add_to_stock: true });
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", remaining: "", note: "" });
  const unit = product.unit || "METRE";
  useEffect(() => {
    if (!product.id) return undefined;
    let alive = true;
    api.get(`/products/lookup?id=${encodeURIComponent(product.id)}&lite=true`)
      .then((r) => { const f = (r.data || []).find((x) => x.product.id === product.id); if (alive && f) setDrums(f.product.drums || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [product.id]);
  const totalRemaining = drums.reduce((s, d) => s + (Number(d.remaining) || 0), 0);

  const addDrum = async () => {
    const len = Number(addForm.length) || 0;
    if (len <= 0) { toast.error("Makara boyu 0'dan büyük olmalı"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/drums`, { label: addForm.label.trim(), length: len, note: addForm.note.trim(), add_to_stock: addForm.add_to_stock });
      setDrums(data.drums || []);
      setAddForm({ label: "", length: "", note: "", add_to_stock: true });
      toast.success("Makara eklendi" + (addForm.add_to_stock ? " · boy stoğa işlendi" : ""));
      onChanged?.(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const startEdit = (d) => { setEditId(d.id); setEditForm({ label: d.label || "", remaining: d.remaining ?? "", note: d.note || "" }); };
  const saveEdit = async (d) => {
    try {
      const { data } = await api.put(`/products/${product.id}/drums/${d.id}`, { label: editForm.label.trim(), remaining: Number(editForm.remaining) || 0, note: editForm.note.trim() });
      setDrums(data.drums || []); setEditId(null); toast.success("Makara güncellendi"); onChanged?.(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const delDrum = async (d) => {
    if (!await confirm(`"${d.label}" makarasını silmek istediğinize emin misiniz?`)) return;
    try {
      const { data } = await api.delete(`/products/${product.id}/drums/${d.id}`);
      setDrums(data.drums || []); toast.success("Makara silindi"); onChanged?.(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/40 overflow-hidden" data-testid={`drum-manager-${product.id}`}>
      <div className="px-4 py-2.5 border-b border-blue-200 bg-blue-100/50 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-widest text-blue-800 flex items-center gap-1.5"><Cable className="h-4 w-4" />Makara / Rulo Takibi</div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="rounded-full bg-white border border-blue-200 px-2 py-0.5 font-semibold text-blue-800" data-testid="drum-count">{drums.length} makara</span>
          <span className="rounded-full bg-white border border-blue-200 px-2 py-0.5 font-semibold text-blue-800 tabular-nums" data-testid="drum-total-remaining">Kalan: {NUM(totalRemaining)} {unit}</span>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {drums.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3" data-testid="drum-empty">Henüz makara/rulo tanımlı değil.{isAdmin ? " Aşağıdan ekleyin." : ""}</p>
        ) : drums.map((d) => {
          const pct = d.initial > 0 ? Math.max(0, Math.min(100, Math.round((Number(d.remaining) / Number(d.initial)) * 100))) : 0;
          const isEd = editId === d.id;
          return (
            <div key={d.id} className="rounded-lg border bg-card p-2.5" data-testid={`drum-row-${d.id}`}>
              {isEd ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} placeholder="Etiket" data-testid="drum-edit-label" />
                    <Input type="number" value={editForm.remaining} onChange={(e) => setEditForm({ ...editForm, remaining: e.target.value })} placeholder={`Kalan (${unit})`} data-testid="drum-edit-remaining" />
                  </div>
                  <Input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} placeholder="Not (ops.)" data-testid="drum-edit-note" />
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>İptal</Button>
                    <Button size="sm" onClick={() => saveEdit(d)} data-testid="drum-edit-save">Kaydet</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm truncate">{d.label}</span>
                      <span className="text-xs tabular-nums text-muted-foreground shrink-0">{NUM(d.remaining)} / {NUM(d.initial)} {unit}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} /></div>
                    {d.note && <div className="text-[11px] text-muted-foreground italic mt-0.5">{d.note}</div>}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-0.5 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEdit(d)} data-testid={`drum-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => delDrum(d)} data-testid={`drum-del-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {isAdmin && (
          <div className="rounded-lg border border-dashed border-blue-300 bg-white p-2.5 space-y-2" data-testid="drum-add-form">
            <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Yeni Makara / Rulo Ekle</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })} placeholder="Etiket (ops.)" data-testid="drum-add-label" />
              <Input type="number" value={addForm.length} onChange={(e) => setAddForm({ ...addForm, length: e.target.value })} placeholder={`Boy (${unit})`} data-testid="drum-add-length" />
              <Input value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} placeholder="Not (ops.)" data-testid="drum-add-note" />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Checkbox checked={addForm.add_to_stock} onCheckedChange={(v) => setAddForm({ ...addForm, add_to_stock: !!v })} data-testid="drum-add-stock" />
                Bu boyu stoğa da ekle
              </label>
              <Button size="sm" onClick={addDrum} disabled={busy} data-testid="drum-add-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}Makara Ekle</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductCard({ data, isAdmin, onMove, onEdit, onDelete, onQuote }) {
  const { product: p, recent_movements = [], stock_value, movement_count, orders = [], sale_price = 0 } = data;
  const activeOrders = (orders || []).filter((o) => o.status !== "tamamlandi");
  const critical = p.critical_level > 0 && p.quantity <= p.critical_level;
  const time = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "-"; } };
  const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
  const out = (p.quantity || 0) <= 0;
  const margin = (isAdmin && sale_price > 0) ? Math.round(((sale_price - (p.cost || 0)) / sale_price) * 100) : null;
  return (
    <div className={cn("relative bg-card border border-l-4 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow", out ? "border-l-red-500" : critical ? "border-l-amber-500" : "border-l-emerald-500")} data-testid={`product-card-${p.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
        <div className="p-5 border-b lg:border-b-0 lg:border-r bg-slate-50/60">
          <div className="flex gap-3">
            {imgs[0] ? <img src={fileUrl(imgs[0])} alt="" className="h-20 w-20 rounded-lg object-contain bg-white border shrink-0" />
              : <div className="h-20 w-20 rounded-lg bg-muted flex items-center justify-center shrink-0"><Package className="h-7 w-7 text-muted-foreground/40" /></div>}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap mb-1">
                {out ? <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-0 text-[9px] px-1.5">Tükendi</Badge> : critical ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[9px] px-1.5 gap-0.5" data-testid={`critical-badge-${p.id}`}><AlertTriangle className="h-2.5 w-2.5" />Kritik</Badge> : <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0 text-[9px] px-1.5">Stokta</Badge>}
                {activeOrders.length > 0 && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0 text-[9px] gap-0.5 px-1.5" data-testid={`card-requested-${p.id}`}><Clock className="h-2.5 w-2.5" />Talepte ({activeOrders.reduce((s, o) => s + (o.requested_qty || 0), 0)})</Badge>}
              </div>
              <h3 className="font-head text-lg font-black leading-tight tracking-tight">{pname(p)}</h3>
              <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                {p.brand && <span className="inline-flex items-center gap-1 rounded-full bg-secondary/10 text-secondary px-2 py-0.5 font-semibold"><Tag className="h-3 w-3" />{p.brand}</span>}
                {p.code && <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono">{p.code}</span>}
                {p.barcode && <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono"><Barcode className="h-3 w-3" />{p.barcode}</span>}
                {(p.barcodes || []).map((b, bi) => (b ? <span key={bi} className="inline-flex items-center gap-1 bg-muted/70 text-muted-foreground px-2 py-0.5 rounded font-mono" data-testid={`detail-extra-barcode-${bi}`}><Barcode className="h-3 w-3" />{b}</span> : null))}
              </div>
              {p.category && <div className="text-[11px] text-muted-foreground mt-1.5 uppercase tracking-wider font-semibold">{p.category}</div>}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Mevcut Stok" value={<><span className={out ? "text-red-600" : critical ? "text-amber-600" : ""}>{NUM(p.quantity)} <span className="text-xs font-semibold text-muted-foreground">{p.unit}</span></span><span className="block mt-1"><StockSplit p={p} testid="detail-stock-split" /></span></>} icon={Boxes} />
            <Info label="Kritik Seviye" value={p.critical_level > 0 ? <>{NUM(p.critical_level)} <span className="text-xs font-semibold text-muted-foreground">{p.unit}</span></> : "—"} icon={AlertTriangle} />
            {isAdmin && <Info label="Satış Fiyatı" value={<>{TRY(sale_price)}{sale_price !== p.price && <span className="block text-[10px] text-muted-foreground font-normal">Liste: {TRY(p.price)}</span>}{p.currency && p.currency !== "TL" && <span className="block text-[10px] text-muted-foreground font-normal">{NUM(p.list_orig ?? p.list_price)} {p.currency}</span>}</>} highlight />}
            {isAdmin && <Info label={margin !== null ? `Stok Değeri · %${margin} marj` : "Stok Değeri"} value={TRY(stock_value)} icon={Wallet} />}
          </div>
          <div className="flex gap-1.5 pt-1 flex-wrap">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onMove(p)} data-testid={`move-${p.id}`}><ClipboardEdit className="h-4 w-4 mr-1.5" />Giriş / Çıkış / Sayım</Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4 mr-1.5" />Düzenle</Button>
            {isAdmin && <Button size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary/5" onClick={() => onQuote(p)} data-testid={`quote-add-${p.id}`}><FileText className="h-4 w-4 mr-1.5" />Teklife Ekle</Button>}
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`del-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
          </div>
        </div>

        <div className="p-5 border-t lg:border-t-0 lg:border-l">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1 font-head"><Clock className="h-3.5 w-3.5" />Son Hareketler <span className="tnum">({movement_count})</span></div>
          {recent_movements.length === 0 ? <p className="text-sm text-muted-foreground">Henüz hareket yok</p> :
            recent_movements.slice(0, 5).map((m) => {
              const mm = moveMeta(m.type);
              return (
              <div key={m.id} className={cn("text-sm py-1.5 pl-2.5 border-l-2 mb-1 last:mb-0", mm.stripe)} data-testid={`movement-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs">{moveLabel[m.type] || m.type}</span>
                  <span className="tnum text-xs text-muted-foreground">{NUM(m.old_qty)} <ArrowRight className="inline h-3 w-3" /> {NUM(m.new_qty)}</span>
                </div>
                {locInfo(m) && <div className="text-[11px] font-semibold text-teal-700" data-testid={`movement-loc-${m.id}`}>{locInfo(m)}</div>}
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  {m.quote_no ? <><FileText className="h-3 w-3 text-primary" /><span className="text-primary font-medium">Teklif #{m.quote_no}</span></> : <><User className="h-3 w-3" />{m.user_name}</>}
                  <span>· {time(m.created_at)}</span>
                </div>
                {m.note && !m.quote_no && <div className="text-[11px] text-muted-foreground italic">{m.note}</div>}
              </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function ProductDetailView({ data, isAdmin, loading, onMove, onEdit, onDelete, onQuote, onDrumChanged }) {
  const { product: p, recent_movements = [], stock_value, movement_count, orders = [], sale_price = 0, unit_profit = 0 } = data;
  const activeOrders = (orders || []).filter((o) => o.status !== "tamamlandi");
  const critical = p.critical_level > 0 && p.quantity <= p.critical_level;
  const imgs = (p.images && p.images.length) ? p.images : (p.image_url ? [p.image_url] : []);
  const [hero, setHero] = useState(imgs[0] || "");
  const [showHist, setShowHist] = useState(false);
  const time = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "-"; } };
  const margin = (isAdmin && sale_price > 0) ? Math.round(((sale_price - (p.cost || 0)) / sale_price) * 100) : null;
  return (
    <div className="space-y-5" data-testid={`product-detail-${p.id}`}>
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="sm:w-52 shrink-0">
          {hero ? <img src={fileUrl(hero)} alt="" className="w-full h-48 rounded-xl object-contain bg-white border" />
            : <div className="w-full h-48 rounded-xl bg-muted flex items-center justify-center"><Package className="h-12 w-12 text-muted-foreground/30" /></div>}
          {imgs.length > 1 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {imgs.map((im, i) => (
                <button key={i} onClick={() => setHero(im)} className={`h-12 w-12 rounded-lg overflow-hidden border-2 ${hero === im ? "border-primary" : "border-transparent"}`} data-testid={`detail-thumb-${i}`}>
                  <img src={fileUrl(im)} alt="" className="h-full w-full object-contain bg-white" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 flex-wrap">
            <h2 className="font-head text-2xl font-black leading-tight">{pname(p)}</h2>
            {critical && <Badge variant="destructive" data-testid={`detail-critical-${p.id}`}>Kritik seviye</Badge>}
            {activeOrders.length > 0 && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-0"><Clock className="h-3 w-3 mr-1" />Talepte</Badge>}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
            {p.brand && <span className="inline-flex items-center gap-1 bg-secondary/10 text-secondary px-2 py-0.5 rounded"><Tag className="h-3 w-3" />{p.brand}</span>}
            {p.code && <span className="bg-muted px-2 py-0.5 rounded font-mono">{p.code}</span>}
            {p.barcode && <span className="inline-flex items-center gap-1 bg-muted px-2 py-0.5 rounded font-mono"><Barcode className="h-3 w-3" />{p.barcode}</span>}
            {p.category && <span className="bg-muted px-2 py-0.5 rounded">{p.category}</span>}
          </div>
          {p.description && <p className="text-sm text-muted-foreground mt-2">{p.description}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
            <DInfo label="Mevcut Stok" value={<><span className={critical ? "text-destructive" : ""}>{NUM(p.quantity)} {p.unit}</span><span className="block mt-1"><StockSplit p={p} testid="edit-stock-split" /></span></>} />
            <DInfo label="Kritik Seviye" value={p.critical_level > 0 ? `${NUM(p.critical_level)} ${p.unit}` : "—"} />
            {isAdmin && <DInfo label="Satış Fiyatı" value={loading ? "—" : TRY(sale_price)} highlight />}
            {isAdmin && <DInfo label="Maliyet" value={TRY(p.cost)} />}
            {isAdmin && <DInfo label="KDV Dahil Maliyet" value={TRY((p.cost || 0) * (1 + (p.kdv_rate != null ? p.kdv_rate : 20) / 100))} />}
            {isAdmin && <DInfo label="Liste Fiyatı" value={<>{TRY(p.price)}{p.currency && p.currency !== "TL" && <span className="block text-[10px] text-muted-foreground font-normal">{NUM(p.list_orig ?? p.list_price)} {p.currency}</span>}</>} />}
            {isAdmin && <DInfo label="Birim Kâr" value={loading ? "—" : TRY(unit_profit)} />}
            {isAdmin && !loading && margin !== null && <DInfo label="Kâr Marjı" value={`%${margin}`} />}
            {isAdmin && <DInfo label="Stok Değeri (Maliyet)" value={loading ? "—" : TRY(stock_value)} highlight />}
          </div>
          {isAdmin && (p.price_general != null || p.price_elec_vat != null || p.price_elec_novat != null) && (
            <div className="mt-3 rounded-lg border border-secondary/30 bg-secondary/5 p-3" data-testid={`detail-manual-prices-${p.id}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-secondary mb-1.5">Manuel Satış Fiyatları (KDV dahil · iskontoyu ezer)</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[10px] text-muted-foreground">Genel</div><div className="font-head font-black tabular-nums text-sm">{p.price_general != null ? TRY(p.price_general) : "—"}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Elk. Faturalı</div><div className="font-head font-black tabular-nums text-sm">{p.price_elec_vat != null ? TRY(p.price_elec_vat) : "—"}</div></div>
                <div><div className="text-[10px] text-muted-foreground">Elk. KDV'siz</div><div className="font-head font-black tabular-nums text-sm">{p.price_elec_novat != null ? TRY(p.price_elec_novat) : "—"}</div></div>
              </div>
            </div>
          )}
          <div className="flex gap-1.5 pt-4 flex-wrap">
            <Button size="sm" variant="secondary" onClick={() => onMove(p)} data-testid={`detail-move-${p.id}`}><ClipboardEdit className="h-4 w-4 mr-1.5" />Stok Hareketi</Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`detail-edit-btn-${p.id}`}><Pencil className="h-4 w-4 mr-1.5" />Düzenle</Button>
            {isAdmin && <Button size="sm" variant="outline" className="border-primary/40 text-primary hover:bg-primary/5" onClick={() => onQuote(p)} data-testid={`detail-quote-add-${p.id}`}><FileText className="h-4 w-4 mr-1.5" />Teklife Ekle</Button>}
            {isAdmin && <Button size="sm" variant="outline" onClick={() => setShowHist((v) => !v)} data-testid={`detail-history-btn-${p.id}`}><History className="h-4 w-4 mr-1.5" />Geçmiş</Button>}
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)} data-testid={`detail-del-btn-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
          </div>
        </div>
      </div>

      {(data.is_cable ?? isCableProduct(p)) && (
        <DrumManager product={p} isAdmin={isAdmin} onChanged={onDrumChanged} />
      )}

      {activeOrders.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Açık Sipariş Talepleri</div>
          {activeOrders.map((o) => <div key={o.id} className="text-sm text-amber-800">{NUM(o.requested_qty)} {p.unit} · {o.status}</div>)}
        </div>
      )}

      {isAdmin && <PurchaseHistoryPanel productId={p.id} compact />}

      <div className="rounded-xl border overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Stok Hareket Geçmişi ({movement_count})</div>
        {recent_movements.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">Henüz hareket yok</p> : (
          <div className="max-h-72 overflow-y-auto divide-y">
            {recent_movements.map((m) => (
              <div key={m.id} className="px-4 py-2.5" data-testid={`detail-movement-${m.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{moveLabel[m.type] || m.type}</span>
                  <span className="tabular-nums text-sm text-muted-foreground">{NUM(m.old_qty)} <ArrowRight className="inline h-3 w-3" /> {NUM(m.new_qty)}</span>
                </div>
                {locInfo(m) && <div className="text-xs font-semibold text-teal-700" data-testid={`detail-movement-loc-${m.id}`}>{locInfo(m)}</div>}
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  {m.quote_no ? <><FileText className="h-3 w-3 text-primary" /><span className="text-primary font-medium">Teklif #{m.quote_no}</span></> : <><User className="h-3 w-3" />{m.user_name}</>}
                  <span>· {time(m.created_at)}</span>
                </div>
                {m.note && !m.quote_no && <div className="text-[11px] text-muted-foreground italic mt-0.5">{m.note}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      {isAdmin && showHist && (
        <div className="rounded-xl border overflow-hidden" data-testid={`detail-history-${p.id}`}>
          <div className="px-4 py-2.5 border-b bg-muted/40 text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1"><History className="h-3.5 w-3.5" />Değişiklik Geçmişi (Fiyat / Maliyet / Stok vb.)</div>
          <div className="p-4">
            <RecordHistory entityType="product" entityId={p.id} />
          </div>
        </div>
      )}
    </div>
  );
}

function DInfo({ label, value, highlight }) {
  return (
    <div className="bg-muted/40 rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-head font-black tabular-nums mt-1 text-sm ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
function Info({ label, value, icon: Icon, highlight }) {
  return (
    <div className="bg-muted/40 rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className={`font-head font-black tabular-nums mt-1 text-sm ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
